import * as THREE from "three";
import type {
  ChapterId,
  Collider,
  Interactable,
  MovingHazard,
  WorldContext,
} from "./types";

interface WorldCallbacks {
  interact: (id: string) => void;
  enabled: (id: string) => boolean;
  /**
   * Vrai quand un objet transportable est déjà dans les bras, dans le coffre ou
   * dans l'inventaire. Le monde est reconstruit à chaque chapitre : sans ça, une
   * tour beige repousserait sur le plancher à chaque rechargement.
   */
  stowed?: (cargoId: string) => boolean;
}

interface RoomSize {
  width: number;
  depth: number;
  height: number;
}

type Updater = (elapsed: number, delta: number) => void;

interface BuildState {
  scene: THREE.Scene;
  colliders: Collider[];
  interactables: Interactable[];
  hazards: MovingHazard[];
  callbacks: WorldCallbacks;
  updaters: Updater[];
  room: RoomSize;
  lightBudget: number;
}

export type WallSide = "north" | "south" | "west" | "east";

const WALL_THICKNESS = 0.35;

/**
 * Point lights are the single most expensive thing in this scene graph, so the
 * builders request them through a budget instead of sprinkling one per fixture.
 * Everything else fakes its glow with emissive panels and additive sprites.
 */
const LIGHT_BUDGET = 7;

const MAT = {
  asphalt: 0x14181f,
  concrete: 0x2b303c,
  wall: 0x232935,
  dark: 0x0b0f18,
  table: 0x35303b,
  green: 0x9cff57,
  teal: 0x57f0d2,
  amber: 0xf4c95d,
  red: 0xff5d73,
  purple: 0xa777ff,
  blue: 0x5d8cff,
  pink: 0xff6fb5,
  orange: 0xf08a3c,
  white: 0xe8edf7,
  beige: 0xc6b28a,
};

export function buildWorld(
  chapter: ChapterId,
  scene: THREE.Scene,
  callbacks: WorldCallbacks,
): WorldContext {
  const state: BuildState = {
    scene,
    colliders: [],
    interactables: [],
    hazards: [],
    callbacks,
    updaters: [],
    room: { width: 32, depth: 32, height: 4.8 },
    lightBudget: LIGHT_BUDGET,
  };

  scene.background = new THREE.Color(MAT.dark);
  scene.fog = new THREE.FogExp2(MAT.dark, 0.018);

  switch (chapter) {
    case "prologue":
      return buildPrologue(state);
    case "parking":
      return buildParking(state);
    case "registration":
      return buildRegistration(state);
    case "checkpoint":
      return buildCheckpoint(state);
    case "hall":
      return buildHall(state);
    case "seat":
      return buildSeat(state);
    case "network":
      return buildNetwork(state);
    case "credits":
      return buildCreditsWorld(state);
  }
}

/* ------------------------------------------------------------------ *
 * Shared caches
 * ------------------------------------------------------------------ */

const textureCache = new Map<string, THREE.CanvasTexture>();
const materialCache = new Map<string, THREE.Material>();
const geometryCache = new Map<string, THREE.BufferGeometry>();

function cachedGeometry<T extends THREE.BufferGeometry>(
  key: string,
  create: () => T,
): T {
  const existing = geometryCache.get(key);
  if (existing) return existing as T;
  const geometry = create();
  geometryCache.set(key, geometry);
  return geometry;
}

interface SurfaceOptions {
  roughness?: number;
  metalness?: number;
  emissive?: number;
  emissiveIntensity?: number;
  opacity?: number;
  flatShading?: boolean;
  side?: THREE.Side;
}

function surface(color: number, options: SurfaceOptions = {}): THREE.MeshStandardMaterial {
  const key = [
    "std",
    color,
    options.roughness ?? 0.72,
    options.metalness ?? 0.05,
    options.emissive ?? 0,
    options.emissiveIntensity ?? 0,
    options.opacity ?? 1,
    options.flatShading ? 1 : 0,
    options.side ?? THREE.FrontSide,
  ].join("|");
  const existing = materialCache.get(key);
  if (existing) return existing as THREE.MeshStandardMaterial;
  const material = new THREE.MeshStandardMaterial({
    color,
    roughness: options.roughness ?? 0.72,
    metalness: options.metalness ?? 0.05,
    emissive: options.emissive ?? 0x000000,
    emissiveIntensity: options.emissiveIntensity ?? (options.emissive ? 0.6 : 0),
    transparent: options.opacity !== undefined && options.opacity < 1,
    opacity: options.opacity ?? 1,
    flatShading: options.flatShading ?? false,
    side: options.side ?? THREE.FrontSide,
  });
  materialCache.set(key, material);
  return material;
}

function unlit(
  color: number,
  options: { opacity?: number; additive?: boolean; side?: THREE.Side } = {},
): THREE.MeshBasicMaterial {
  const key = [
    "basic",
    color,
    options.opacity ?? 1,
    options.additive ? 1 : 0,
    options.side ?? THREE.FrontSide,
  ].join("|");
  const existing = materialCache.get(key);
  if (existing) return existing as THREE.MeshBasicMaterial;
  const material = new THREE.MeshBasicMaterial({
    color,
    transparent: options.opacity !== undefined || Boolean(options.additive),
    opacity: options.opacity ?? 1,
    blending: options.additive ? THREE.AdditiveBlending : THREE.NormalBlending,
    depthWrite: !options.additive && (options.opacity ?? 1) >= 1,
    side: options.side ?? THREE.FrontSide,
    toneMapped: false,
  });
  materialCache.set(key, material);
  return material;
}

/* ------------------------------------------------------------------ *
 * Canvas textures
 * ------------------------------------------------------------------ */

function hex(color: number): string {
  return `#${color.toString(16).padStart(6, "0")}`;
}

function rgba(color: number, alpha: number): string {
  return `rgba(${(color >> 16) & 255}, ${(color >> 8) & 255}, ${color & 255}, ${alpha})`;
}

function seeded(seed: number): () => number {
  let value = seed >>> 0 || 1;
  return () => {
    value = (value * 1664525 + 1013904223) >>> 0;
    return value / 0xffffffff;
  };
}

function canvasTexture(
  key: string,
  width: number,
  height: number,
  draw: (ctx: CanvasRenderingContext2D, width: number, height: number) => void,
): THREE.CanvasTexture {
  const existing = textureCache.get(key);
  if (existing) return existing;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;
  draw(ctx, width, height);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  textureCache.set(key, texture);
  return texture;
}

/**
 * Sets a font that actually fits `maxWidth`. Long marketing lines used to run
 * straight off the edge of their own sign.
 */
function fitFont(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  weight: number,
  family: string,
  startSize: number,
): void {
  let size = Math.max(6, startSize);
  ctx.font = `${weight} ${Math.round(size)}px ${family}`;
  let guard = 0;
  while (ctx.measureText(text).width > maxWidth && size > 7 && guard < 40) {
    size *= 0.92;
    ctx.font = `${weight} ${Math.round(size)}px ${family}`;
    guard += 1;
  }
}

function scanlines(ctx: CanvasRenderingContext2D, width: number, height: number, step = 4): void {
  ctx.fillStyle = "rgba(0, 0, 0, .26)";
  for (let y = 0; y < height; y += step) {
    ctx.fillRect(0, y, width, 1);
  }
}

function vignette(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const gradient = ctx.createRadialGradient(
    width / 2,
    height / 2,
    Math.min(width, height) * 0.16,
    width / 2,
    height / 2,
    Math.max(width, height) * 0.72,
  );
  gradient.addColorStop(0, "rgba(0, 0, 0, 0)");
  gradient.addColorStop(1, "rgba(0, 0, 0, .55)");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
}

export interface ScreenContent {
  /** Small caption drawn in the header strip. */
  title?: string;
  /** Body rows. A row prefixed with `!` is rendered as the hero line. */
  lines: string[];
  accent: number;
  footer?: string;
  ratio?: number;
  /** `crt` bows the content, `panel` stays flat and clean. */
  variant?: "crt" | "panel";
}

function screenTexture(content: ScreenContent): THREE.CanvasTexture {
  const ratio = content.ratio ?? 16 / 9;
  const width = 512;
  const height = Math.round(width / ratio);
  const key = `screen|${content.title ?? ""}|${content.lines.join("¦")}|${content.accent}|${
    content.footer ?? ""
  }|${ratio}|${content.variant ?? "crt"}`;

  return canvasTexture(key, width, height, (ctx, w, h) => {
    const accent = content.accent;
    const backdrop = ctx.createLinearGradient(0, 0, 0, h);
    backdrop.addColorStop(0, "#0a1119");
    backdrop.addColorStop(0.55, "#050a10");
    backdrop.addColorStop(1, "#02050a");
    ctx.fillStyle = backdrop;
    ctx.fillRect(0, 0, w, h);

    // Phosphor bloom behind the type keeps the panel from looking like a decal.
    const bloom = ctx.createRadialGradient(w / 2, h * 0.5, 8, w / 2, h * 0.5, w * 0.62);
    bloom.addColorStop(0, rgba(accent, 0.2));
    bloom.addColorStop(1, rgba(accent, 0));
    ctx.fillStyle = bloom;
    ctx.fillRect(0, 0, w, h);

    const pad = Math.round(h * 0.07);
    if (content.title) {
      ctx.fillStyle = rgba(accent, 0.18);
      ctx.fillRect(pad, pad, w - pad * 2, Math.round(h * 0.14));
      ctx.fillStyle = hex(accent);
      ctx.font = `700 ${Math.round(h * 0.09)}px ui-monospace, "SF Mono", Consolas, monospace`;
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(content.title.toUpperCase(), pad + Math.round(h * 0.05), pad + Math.round(h * 0.07));
    }

    const top = content.title ? pad * 2 + h * 0.14 : pad;
    const bottom = content.footer ? h - pad * 2 - h * 0.1 : h - pad;
    const rows = content.lines.length || 1;
    const slot = (bottom - top) / rows;

    content.lines.forEach((raw, index) => {
      const hero = raw.startsWith("!");
      const text = hero ? raw.slice(1) : raw;
      const size = Math.min(slot * (hero ? 0.86 : 0.62), w * 0.16);
      fitFont(ctx, text, w - pad * 2.2, hero ? 900 : 700, 'ui-monospace, "SF Mono", Consolas, monospace', size);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const y = top + slot * index + slot / 2;
      if (hero) {
        ctx.fillStyle = rgba(accent, 0.32);
        ctx.fillText(text, w / 2 + 3, y + 3);
        ctx.fillStyle = hex(accent);
      } else {
        ctx.fillStyle = "rgba(226, 236, 252, .82)";
      }
      ctx.fillText(text, w / 2, y);
    });

    if (content.footer) {
      ctx.fillStyle = rgba(accent, 0.62);
      ctx.font = `700 ${Math.round(h * 0.07)}px ui-monospace, Consolas, monospace`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(content.footer.toUpperCase(), w / 2, h - pad - h * 0.04);
    }

    if (content.variant !== "panel") scanlines(ctx, w, h, 4);

    ctx.strokeStyle = rgba(accent, 0.55);
    ctx.lineWidth = 4;
    ctx.strokeRect(2, 2, w - 4, h - 4);
    vignette(ctx, w, h);
  });
}

function signTexture(
  lines: string[],
  accent: number,
  options: { ratio?: number; background?: number; kicker?: string } = {},
): THREE.CanvasTexture {
  const ratio = options.ratio ?? 4;
  const width = 640;
  const height = Math.round(width / ratio);
  const background = options.background ?? 0x101623;
  const key = `sign|${lines.join("¦")}|${accent}|${ratio}|${background}|${options.kicker ?? ""}`;

  return canvasTexture(key, width, height, (ctx, w, h) => {
    ctx.fillStyle = hex(background);
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = rgba(accent, 0.14);
    ctx.fillRect(0, 0, w, Math.round(h * 0.16));
    ctx.fillStyle = rgba(accent, 0.9);
    ctx.fillRect(0, h - Math.round(h * 0.07), w, Math.round(h * 0.07));

    if (options.kicker) {
      ctx.fillStyle = rgba(accent, 0.85);
      ctx.font = `800 ${Math.round(h * 0.11)}px system-ui, sans-serif`;
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(options.kicker.toUpperCase(), Math.round(w * 0.035), Math.round(h * 0.09));
    }

    const top = options.kicker ? h * 0.2 : h * 0.08;
    const slot = (h * 0.86 - top) / (lines.length || 1);
    lines.forEach((line, index) => {
      const size = Math.min(slot * 0.78, w * 0.13);
      fitFont(ctx, line, w * 0.92, 900, "system-ui, sans-serif", size);
      ctx.fillStyle = index === 0 ? "#f4f8ff" : rgba(accent, 0.92);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(line, w / 2, top + slot * index + slot / 2);
    });
  });
}

function posterTexture(
  title: string,
  subtitle: string,
  accent: number,
  seed: number,
): THREE.CanvasTexture {
  const width = 384;
  const height = 512;
  return canvasTexture(`poster|${title}|${subtitle}|${accent}|${seed}`, width, height, (ctx, w, h) => {
    ctx.fillStyle = "#10131f";
    ctx.fillRect(0, 0, w, h);

    const random = seeded(seed);
    ctx.fillStyle = rgba(accent, 0.16);
    for (let i = 0; i < 26; i += 1) {
      const size = 12 + random() * 90;
      ctx.fillRect(random() * w, random() * h, size, size * 0.14);
    }

    ctx.fillStyle = rgba(accent, 0.9);
    ctx.beginPath();
    ctx.arc(w / 2, h * 0.36, w * 0.28, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#10131f";
    ctx.beginPath();
    ctx.arc(w / 2, h * 0.36, w * 0.2, 0, Math.PI * 2);
    ctx.fill();

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#f6f9ff";
    title.split("\n").forEach((line, index) => {
      fitFont(ctx, line, w * 0.86, 900, "system-ui, sans-serif", w * 0.16);
      ctx.fillText(line, w / 2, h * 0.66 + index * w * 0.17);
    });
    ctx.fillStyle = rgba(accent, 0.86);
    fitFont(ctx, subtitle, w * 0.9, 800, "system-ui, sans-serif", w * 0.062);
    ctx.fillText(subtitle, w / 2, h * 0.92);

    ctx.strokeStyle = rgba(accent, 0.5);
    ctx.lineWidth = 6;
    ctx.strokeRect(10, 10, w - 20, h - 20);
  });
}

function floorTexture(kind: "asphalt" | "carpet" | "concrete" | "wood"): THREE.CanvasTexture {
  return canvasTexture(`floor|${kind}`, 256, 256, (ctx, w, h) => {
    const base =
      kind === "asphalt" ? "#b0b3b8" : kind === "carpet" ? "#c8c2d2" : kind === "wood" ? "#cbb18d" : "#c6c9d0";
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, w, h);

    const random = seeded(kind.length * 7919 + 13);
    const count = kind === "asphalt" ? 2600 : kind === "carpet" ? 3400 : 1200;
    for (let i = 0; i < count; i += 1) {
      const shade = Math.round(70 + random() * 120);
      ctx.fillStyle = `rgba(${shade}, ${shade}, ${shade}, ${kind === "carpet" ? 0.14 : 0.2})`;
      const size = kind === "carpet" ? 0.6 + random() * 1.4 : 0.5 + random() * 2.2;
      ctx.fillRect(random() * w, random() * h, size, size);
    }

    if (kind === "wood") {
      ctx.strokeStyle = "rgba(90, 66, 42, .22)";
      ctx.lineWidth = 2;
      for (let y = 0; y < h; y += 32) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
      }
    }
    if (kind === "concrete" || kind === "asphalt") {
      ctx.strokeStyle = "rgba(40, 44, 54, .24)";
      ctx.lineWidth = 2;
      ctx.strokeRect(1, 1, w - 2, h - 2);
    }
  });
}

/** One soft radial alpha map, reused by every glow, marker and light bloom. */
function glowTexture(): THREE.CanvasTexture {
  return canvasTexture("glow", 128, 128, (ctx, w, h) => {
    const gradient = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w / 2);
    gradient.addColorStop(0, "rgba(255, 255, 255, 1)");
    gradient.addColorStop(0.35, "rgba(255, 255, 255, .5)");
    gradient.addColorStop(1, "rgba(255, 255, 255, 0)");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, w, h);
  });
}

function labelTexture(text: string, accent: number): THREE.CanvasTexture {
  const lines = text.split("\n");
  const width = 512;
  const height = Math.max(96, lines.length * 96);
  return canvasTexture(`label|${text}|${accent}`, width, height, (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = "rgba(6, 9, 16, .88)";
    ctx.beginPath();
    ctx.roundRect(6, 6, w - 12, h - 12, 22);
    ctx.fill();
    ctx.strokeStyle = rgba(accent, 0.85);
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.roundRect(9, 9, w - 18, h - 18, 20);
    ctx.stroke();
    ctx.fillStyle = "#f2f6ff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const size = Math.round(Math.min(h / lines.length, w / 9) * 0.62);
    const lineHeight = h / (lines.length + 0.4);
    lines.forEach((line, index) => {
      fitFont(ctx, line, w * 0.88, 900, "system-ui, sans-serif", size);
      ctx.fillText(line, w / 2, h / 2 + (index - (lines.length - 1) / 2) * lineHeight);
    });
  });
}

/* ------------------------------------------------------------------ *
 * Primitives
 * ------------------------------------------------------------------ */

function box(
  state: BuildState,
  x: number,
  y: number,
  z: number,
  width: number,
  height: number,
  depth: number,
  color: number,
  collider: boolean,
  options: SurfaceOptions & { castShadow?: boolean } = {},
): THREE.Mesh {
  const mesh = new THREE.Mesh(
    cachedGeometry(`box|${width}|${height}|${depth}`, () => new THREE.BoxGeometry(width, height, depth)),
    surface(color, options),
  );
  mesh.position.set(x, y, z);
  mesh.castShadow = options.castShadow ?? true;
  mesh.receiveShadow = true;
  state.scene.add(mesh);
  if (collider) {
    state.colliders.push({
      minX: x - width / 2,
      maxX: x + width / 2,
      minZ: z - depth / 2,
      maxZ: z + depth / 2,
    });
  }
  return mesh;
}

function plane(width: number, height: number, color: number, opacity = 1): THREE.Mesh {
  return new THREE.Mesh(
    cachedGeometry(`plane|${width}|${height}`, () => new THREE.PlaneGeometry(width, height)),
    unlit(color, { opacity, side: THREE.DoubleSide }),
  );
}

/**
 * A flat, surface-aligned display. Screens used to be `THREE.Sprite`s, which
 * always turn to face the camera — the text visibly slid off its own monitor
 * whenever the player looked from an angle.
 */
function screenPanel(width: number, height: number, content: ScreenContent): THREE.Mesh {
  const mesh = new THREE.Mesh(
    cachedGeometry(`plane|${width}|${height}`, () => new THREE.PlaneGeometry(width, height)),
    new THREE.MeshBasicMaterial({
      map: screenTexture({ ...content, ratio: content.ratio ?? width / height }),
      toneMapped: false,
    }),
  );
  mesh.renderOrder = 1;
  return mesh;
}

function signPanel(
  width: number,
  height: number,
  lines: string[],
  accent: number,
  options: { background?: number; kicker?: string } = {},
): THREE.Mesh {
  return new THREE.Mesh(
    cachedGeometry(`plane|${width}|${height}`, () => new THREE.PlaneGeometry(width, height)),
    new THREE.MeshBasicMaterial({
      map: signTexture(lines, accent, { ratio: width / height, ...options }),
      toneMapped: false,
    }),
  );
}

/** Cheap fake bloom: an additive billboard that reads as light spill. */
function glowSprite(color: number, size: number, opacity = 0.5): THREE.Sprite {
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: glowTexture(),
      color,
      transparent: true,
      opacity,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    }),
  );
  sprite.scale.set(size, size, 1);
  return sprite;
}

function labelSprite(text: string, color: number, width = 4, height = 0.72): THREE.Sprite {
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: labelTexture(text, color),
      transparent: true,
      depthTest: true,
      depthWrite: false,
    }),
  );
  sprite.scale.set(width, height, 1);
  return sprite;
}

function iconSprite(icon: string, size: number): THREE.Sprite {
  const texture = canvasTexture(`icon|${icon}`, 128, 128, (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    ctx.font = "86px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(icon, w / 2, h / 2 + 4);
  });
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: true,
      depthWrite: false,
    }),
  );
  sprite.scale.set(size, size, 1);
  return sprite;
}

function addPointLight(
  state: BuildState,
  color: number,
  intensity: number,
  distance: number,
  x: number,
  y: number,
  z: number,
): void {
  if (state.lightBudget <= 0) return;
  state.lightBudget -= 1;
  const light = new THREE.PointLight(color, intensity, distance, 2);
  light.position.set(x, y, z);
  state.scene.add(light);
}

function addInteractable(
  state: BuildState,
  id: string,
  label: string,
  object: THREE.Object3D,
  radius: number,
  focusOffset?: THREE.Vector3,
): void {
  state.interactables.push({
    id,
    label,
    object,
    radius,
    focusOffset,
    enabled: () => state.callbacks.enabled(id),
    onInteract: () => state.callbacks.interact(id),
  });
}

/**
 * Retient les colliders qu'un accessoire a poussés, pour que le jeu puisse les
 * retirer quand le joueur l'emporte. Sans ça, un sac à dos ramassé laisse une
 * borne invisible au milieu du salon.
 */
function removable<T extends THREE.Object3D>(state: BuildState, build: () => T): T {
  const before = state.colliders.length;
  const object = build();
  const added = state.colliders.slice(before);
  if (added.length) object.userData.colliders = added;
  return object;
}

function addMarker(object: THREE.Object3D, color: number, radius = 1): void {
  const marker = new THREE.Mesh(
    cachedGeometry(`ring|${radius}`, () => new THREE.TorusGeometry(radius, 0.032, 8, 40)),
    unlit(color, { opacity: 0.5 }),
  );
  marker.rotation.x = Math.PI / 2;
  marker.position.y = 0.035;
  marker.userData.spin = 0.3;
  marker.userData.pulse = true;
  object.add(marker);

  const pool = glowSprite(color, radius * 2.4, 0.16);
  pool.position.y = 0.06;
  object.add(pool);

  const chevron = new THREE.Mesh(
    cachedGeometry("chevron", () => new THREE.ConeGeometry(0.12, 0.28, 4)),
    unlit(color, { opacity: 0.78 }),
  );
  chevron.rotation.x = Math.PI;
  chevron.rotation.y = Math.PI / 4;
  chevron.position.y = 1.42;
  chevron.userData.bob = Math.random() * 6;
  object.add(chevron);
}

/* ------------------------------------------------------------------ *
 * Room shell + wall mounting
 * ------------------------------------------------------------------ */

function interiorBounds(width: number, depth: number, wallThickness = WALL_THICKNESS): Collider {
  const inset = wallThickness / 2;
  return {
    minX: -width / 2 + inset,
    maxX: width / 2 - inset,
    minZ: -depth / 2 + inset,
    maxZ: depth / 2 - inset,
  };
}

/** A sealed portal sits exactly this far inside the playable bounds. */
function sealedExitOffset(dimension: number): number {
  return dimension / 2 - WALL_THICKNESS / 2 - 0.275;
}

/**
 * Anchors decor flush against a wall. Children of the returned group live in
 * "wall space": +Z points into the room, +X runs along the wall.
 */
function wallMount(state: BuildState, side: WallSide, along: number, y: number): THREE.Group {
  const { width, depth } = state.room;
  const group = new THREE.Group();
  const inset = WALL_THICKNESS / 2;
  switch (side) {
    case "north":
      group.position.set(along, y, -depth / 2 + inset);
      group.rotation.y = 0;
      break;
    case "south":
      group.position.set(along, y, depth / 2 - inset);
      group.rotation.y = Math.PI;
      break;
    case "west":
      group.position.set(-width / 2 + inset, y, along);
      group.rotation.y = Math.PI / 2;
      break;
    case "east":
      group.position.set(width / 2 - inset, y, along);
      group.rotation.y = -Math.PI / 2;
      break;
  }
  state.scene.add(group);
  return group;
}

function addGround(
  state: BuildState,
  width: number,
  depth: number,
  color: number,
  kind: "asphalt" | "carpet" | "concrete" | "wood" = "concrete",
): void {
  const texture = floorTexture(kind);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(width / 3.2, depth / 3.2);
  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(width, depth),
    new THREE.MeshStandardMaterial({
      color,
      map: texture,
      roughness: kind === "asphalt" ? 0.97 : kind === "carpet" ? 0.99 : 0.86,
      metalness: 0.015,
    }),
  );
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  state.scene.add(ground);
}

/**
 * Un deuxième sol, ailleurs qu'à l'origine. `addGround` est toujours centré, ce
 * qui suffit pour une pièce mais pas pour une cour collée contre elle.
 */
function addGroundPatch(
  state: BuildState,
  x: number,
  z: number,
  width: number,
  depth: number,
  color: number,
  kind: "asphalt" | "carpet" | "concrete" | "wood" = "asphalt",
): void {
  const texture = floorTexture(kind).clone();
  texture.needsUpdate = true;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(width / 3.2, depth / 3.2);
  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(width, depth),
    new THREE.MeshStandardMaterial({
      color,
      map: texture,
      roughness: kind === "asphalt" ? 0.97 : 0.88,
      metalness: 0.015,
    }),
  );
  ground.rotation.x = -Math.PI / 2;
  ground.position.set(x, 0.004, z);
  ground.receiveShadow = true;
  state.scene.add(ground);
}

/** Une ouverture percée dans un mur, mesurée le long de ce mur. */
export interface WallOpening {
  side: WallSide;
  center: number;
  width: number;
  height?: number;
}

function addRoomWalls(
  state: BuildState,
  width: number,
  depth: number,
  height = 4.8,
  wallColor = MAT.wall,
  opening?: WallOpening,
): void {
  state.room = { width, depth, height };
  const half = height / 2;

  // Walls never cast: the key light sits outside the box, so shadow-casting
  // walls painted a hard diagonal of darkness across every interior floor.
  const shell = { roughness: 0.92, castShadow: false };

  const wall = (side: WallSide): void => {
    const alongDepth = side === "west" || side === "east";
    const span = alongDepth ? depth : width;
    const x = side === "west" ? -width / 2 : side === "east" ? width / 2 : 0;
    const z = side === "north" ? -depth / 2 : side === "south" ? depth / 2 : 0;
    const segment = (from: number, to: number, top: number, solid: boolean): void => {
      const length = to - from;
      if (length <= 0.02 || top <= 0.02) return;
      const middle = (from + to) / 2;
      const y = height - top / 2;
      if (alongDepth) {
        box(state, x, y, middle, WALL_THICKNESS, top, length, wallColor, solid, shell);
      } else {
        box(state, middle, y, z, length, top, WALL_THICKNESS, wallColor, solid, shell);
      }
    };

    if (!opening || opening.side !== side) {
      segment(-span / 2, span / 2, height, true);
      return;
    }

    // Deux jambages pleins et un linteau au-dessus : le passage est un trou dans
    // le mur, pas un pan de mur qui manque.
    const clearance = Math.min(height, opening.height ?? 2.35);
    segment(-span / 2, opening.center - opening.width / 2, height, true);
    segment(opening.center + opening.width / 2, span / 2, height, true);
    segment(
      opening.center - opening.width / 2,
      opening.center + opening.width / 2,
      height - clearance,
      false,
    );
  };

  wall("north");
  wall("south");
  wall("west");
  wall("east");

  const ceiling = new THREE.Mesh(
    new THREE.PlaneGeometry(width - WALL_THICKNESS, depth - WALL_THICKNESS),
    surface(0x333a4a, { roughness: 0.95, side: THREE.DoubleSide }),
  );
  ceiling.rotation.x = Math.PI / 2;
  ceiling.position.y = height;
  ceiling.receiveShadow = true;
  state.scene.add(ceiling);

  // Baseboard + corner columns give the walls a readable scale from any angle.
  const trim = 0.16;
  const inner = WALL_THICKNESS / 2 + 0.04;
  box(state, 0, trim / 2, -depth / 2 + inner, width - WALL_THICKNESS, trim, 0.08, 0x3d4453, false);
  box(state, 0, trim / 2, depth / 2 - inner, width - WALL_THICKNESS, trim, 0.08, 0x3d4453, false);
  box(state, -width / 2 + inner, trim / 2, 0, 0.08, trim, depth - WALL_THICKNESS, 0x3d4453, false);
  box(state, width / 2 - inner, trim / 2, 0, 0.08, trim, depth - WALL_THICKNESS, 0x3d4453, false);

  for (const x of [-width / 2 + 0.34, width / 2 - 0.34]) {
    for (const z of [-depth / 2 + 0.34, depth / 2 - 0.34]) {
      box(state, x, half, z, 0.3, height, 0.3, 0x2e3543, false, {
        metalness: 0.22,
        roughness: 0.62,
        castShadow: false,
      });
    }
  }
}

function addBoundaryFences(state: BuildState, width: number, depth: number): void {
  state.room = { width, depth, height: 2.6 };
  const material = surface(0x6b7686, { roughness: 0.5, metalness: 0.6 });
  const add = (x: number, z: number, w: number, d: number): void => {
    const fence = new THREE.Mesh(new THREE.BoxGeometry(w, 2.6, d), material);
    fence.position.set(x, 1.3, z);
    state.scene.add(fence);

    state.colliders.push({ minX: x - w / 2, maxX: x + w / 2, minZ: z - d / 2, maxZ: z + d / 2 });

    // Chain-link reads as a grid rather than a solid slab.
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(Math.max(w, d), 2.4, Math.round(Math.max(w, d) / 0.5), 6),
      new THREE.MeshBasicMaterial({
        color: 0xaab4c4,
        wireframe: true,
        transparent: true,
        opacity: 0.5,
        toneMapped: false,
      }),
    );
    mesh.position.set(x, 1.35, z);
    if (d > w) mesh.rotation.y = Math.PI / 2;
    state.scene.add(mesh);
  };
  add(0, -depth / 2, width, 0.4);
  add(0, depth / 2, width, 0.4);
  add(-width / 2, 0, 0.4, depth);
  add(width / 2, 0, 0.4, depth);
}

/**
 * Un seul pan de clôture, posé où on veut. Contrairement à `addBoundaryFences`,
 * ça ne réécrit pas `state.room` — dont dépend tout le décor mural.
 */
function addFenceRun(
  state: BuildState,
  x: number,
  z: number,
  width: number,
  depth: number,
  height = 1.9,
): void {
  const post = new THREE.Mesh(
    new THREE.BoxGeometry(width, height, depth),
    surface(0x5a6473, { roughness: 0.62, metalness: 0.35 }),
  );
  post.position.set(x, height / 2, z);
  state.scene.add(post);
  state.colliders.push({
    minX: x - width / 2,
    maxX: x + width / 2,
    minZ: z - depth / 2,
    maxZ: z + depth / 2,
  });

  const span = Math.max(width, depth);
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(span, height - 0.14, Math.round(span / 0.45), 5),
    new THREE.MeshBasicMaterial({
      color: 0x9fabbd,
      wireframe: true,
      transparent: true,
      opacity: 0.42,
      toneMapped: false,
    }),
  );
  mesh.position.set(x, height / 2, z);
  if (depth > width) mesh.rotation.y = Math.PI / 2;
  state.scene.add(mesh);
}

interface LightPreset {
  sky: number;
  ground: number;
  hemi: number;
  ambient: number;
  ambientIntensity: number;
  key: number;
  keyIntensity: number;
  keyPosition: [number, number, number];
  fill: number;
  fillIntensity: number;
  shadowRadius?: number;
}

function addLighting(state: BuildState, preset: LightPreset): void {
  const scene = state.scene;
  scene.add(new THREE.HemisphereLight(preset.sky, preset.ground, preset.hemi));
  scene.add(new THREE.AmbientLight(preset.ambient, preset.ambientIntensity));

  const key = new THREE.DirectionalLight(preset.key, preset.keyIntensity);
  key.position.set(...preset.keyPosition);
  key.castShadow = true;
  // The frustum must cover the room's diagonal: anything sampled outside the
  // shadow map came back fully occluded, painting a hard band across the floor.
  const reach = Math.hypot(state.room.width, state.room.depth) * 0.62;
  key.shadow.mapSize.set(reach > 34 ? 2048 : 1024, reach > 34 ? 2048 : 1024);
  key.shadow.camera.left = -reach;
  key.shadow.camera.right = reach;
  key.shadow.camera.top = reach;
  key.shadow.camera.bottom = -reach;
  key.shadow.camera.far = 140;
  key.shadow.bias = -0.00035;
  key.shadow.normalBias = 0.035;
  key.shadow.radius = preset.shadowRadius ?? 3;
  scene.add(key);

  const fill = new THREE.DirectionalLight(preset.fill, preset.fillIntensity);
  fill.position.set(12, 9, -14);
  scene.add(fill);
}

/* ------------------------------------------------------------------ *
 * Ceiling + atmosphere
 * ------------------------------------------------------------------ */

function addCeilingLights(
  state: BuildState,
  color = 0xe6eaff,
  options: { spacingX?: number; spacingZ?: number; litEvery?: number } = {},
): void {
  const { width, depth, height } = state.room;
  const spacingX = options.spacingX ?? 12;
  const spacingZ = options.spacingZ ?? 14;
  const litEvery = options.litEvery ?? 2;
  let index = 0;

  for (let x = -width / 2 + spacingX / 2; x < width / 2; x += spacingX) {
    for (let z = -depth / 2 + spacingZ / 2; z < depth / 2; z += spacingZ) {
      const panel = new THREE.Mesh(
        cachedGeometry("lightPanel", () => new THREE.BoxGeometry(3.4, 0.08, 0.46)),
        surface(0xf1f3f8, { emissive: color, emissiveIntensity: 2.4, roughness: 0.35 }),
      );
      panel.position.set(x, height - 0.06, z);
      state.scene.add(panel);

      const bloom = glowSprite(color, 5.6, 0.28);
      bloom.position.set(x, height - 0.2, z);
      state.scene.add(bloom);

      if (index % litEvery === 0) {
        addPointLight(state, color, 48, Math.max(spacingX, spacingZ) * 1.5, x, height - 0.5, z);
      }
      index += 1;
    }
  }
}

/** A soft volumetric cone. Reads as haze in a dark hall and costs one mesh. */
function addLightShaft(
  state: BuildState,
  x: number,
  z: number,
  color: number,
  radius: number,
  height: number,
  opacity = 0.05,
): void {
  const shaft = new THREE.Mesh(
    new THREE.CylinderGeometry(0.16, radius, height, 18, 1, true),
    new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
      toneMapped: false,
    }),
  );
  shaft.position.set(x, height / 2, z);
  state.scene.add(shaft);
}

function addTruss(state: BuildState, z: number, span: number, y: number): void {
  const material = surface(0x2a3140, { metalness: 0.55, roughness: 0.45 });
  for (const offset of [-0.28, 0.28]) {
    const beam = new THREE.Mesh(new THREE.BoxGeometry(span, 0.12, 0.12), material);
    beam.position.set(0, y + offset, z);
    state.scene.add(beam);
  }
  for (let x = -span / 2; x <= span / 2; x += 1.6) {
    const strut = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.62, 0.07), material);
    strut.position.set(x, y, z);
    strut.rotation.z = ((x / 1.6) % 2 === 0 ? 1 : -1) * 0.5;
    state.scene.add(strut);
  }
}

function addSpotFixture(state: BuildState, x: number, z: number, y: number, color: number): void {
  const housing = new THREE.Mesh(
    cachedGeometry("spotHousing", () => new THREE.CylinderGeometry(0.14, 0.2, 0.36, 12)),
    surface(0x171c26, { metalness: 0.5, roughness: 0.4 }),
  );
  housing.position.set(x, y - 0.3, z);
  state.scene.add(housing);
  const lens = glowSprite(color, 1.5, 0.5);
  lens.position.set(x, y - 0.48, z);
  state.scene.add(lens);
}

/* ------------------------------------------------------------------ *
 * Wall decor
 * ------------------------------------------------------------------ */

function addWallScreen(
  state: BuildState,
  side: WallSide,
  along: number,
  y: number,
  width: number,
  height: number,
  content: ScreenContent,
): THREE.Group {
  const mount = wallMount(state, side, along, y);
  const bezel = new THREE.Mesh(
    new THREE.BoxGeometry(width + 0.14, height + 0.14, 0.1),
    surface(0x11151e, { roughness: 0.42, metalness: 0.22 }),
  );
  bezel.position.z = 0.05;
  bezel.castShadow = true;
  mount.add(bezel);

  const screen = screenPanel(width, height, content);
  screen.position.z = 0.105;
  mount.add(screen);

  const bloom = glowSprite(content.accent, Math.max(width, height) * 1.5, 0.16);
  bloom.position.z = 0.24;
  mount.add(bloom);
  return mount;
}

function addWallSign(
  state: BuildState,
  side: WallSide,
  along: number,
  y: number,
  width: number,
  height: number,
  lines: string[],
  accent: number,
  kicker?: string,
): THREE.Group {
  const mount = wallMount(state, side, along, y);
  const backing = new THREE.Mesh(
    new THREE.BoxGeometry(width + 0.1, height + 0.1, 0.08),
    surface(0x191f2b, { roughness: 0.6 }),
  );
  backing.position.z = 0.04;
  mount.add(backing);
  const face = signPanel(width, height, lines, accent, { kicker });
  face.position.z = 0.085;
  mount.add(face);
  return mount;
}

function addWallPoster(
  state: BuildState,
  side: WallSide,
  along: number,
  y: number,
  title: string,
  subtitle: string,
  accent: number,
  seed: number,
  width = 1.5,
): THREE.Group {
  const mount = wallMount(state, side, along, y);
  const height = width * (4 / 3);
  const sheet = new THREE.Mesh(
    new THREE.PlaneGeometry(width, height),
    new THREE.MeshBasicMaterial({ map: posterTexture(title, subtitle, accent, seed), toneMapped: false }),
  );
  sheet.position.z = 0.03;
  mount.add(sheet);
  const frame = new THREE.Mesh(
    new THREE.BoxGeometry(width + 0.06, height + 0.06, 0.03),
    surface(0x121722, { roughness: 0.8 }),
  );
  frame.position.z = 0.014;
  mount.add(frame);
  return mount;
}

function addWallClock(state: BuildState, side: WallSide, along: number, y: number, startHour: number): void {
  const mount = wallMount(state, side, along, y);
  const face = new THREE.Mesh(
    cachedGeometry("clockFace", () => new THREE.CylinderGeometry(0.34, 0.34, 0.07, 28)),
    surface(0xe7ecf6, { roughness: 0.5 }),
  );
  face.rotation.x = Math.PI / 2;
  face.position.z = 0.06;
  mount.add(face);

  const rim = new THREE.Mesh(
    cachedGeometry("clockRim", () => new THREE.TorusGeometry(0.35, 0.035, 8, 28)),
    surface(0x1b2230, { metalness: 0.4, roughness: 0.4 }),
  );
  rim.position.z = 0.06;
  mount.add(rim);

  const hands = new THREE.Group();
  hands.position.z = 0.1;
  mount.add(hands);

  const hour = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.17, 0.015), surface(0x161b26));
  hour.position.y = 0.085;
  const hourPivot = new THREE.Group();
  hourPivot.add(hour);
  hands.add(hourPivot);

  const minute = new THREE.Mesh(new THREE.BoxGeometry(0.022, 0.26, 0.013), surface(0x161b26));
  minute.position.y = 0.13;
  const minutePivot = new THREE.Group();
  minutePivot.add(minute);
  hands.add(minutePivot);

  hourPivot.rotation.z = -(startHour % 12) * (Math.PI / 6);
  state.updaters.push((elapsed) => {
    minutePivot.rotation.z = -elapsed * 0.6;
    hourPivot.rotation.z = -(startHour % 12) * (Math.PI / 6) - elapsed * 0.05;
  });
}

function addVent(state: BuildState, side: WallSide, along: number, y: number): void {
  const mount = wallMount(state, side, along, y);
  const frame = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.66, 0.07), surface(0x39404e, { metalness: 0.4 }));
  frame.position.z = 0.035;
  mount.add(frame);
  for (let i = 0; i < 6; i += 1) {
    const slat = new THREE.Mesh(new THREE.BoxGeometry(0.98, 0.055, 0.05), surface(0x1d232e));
    slat.position.set(0, 0.24 - i * 0.095, 0.075);
    slat.rotation.x = 0.4;
    mount.add(slat);
  }
}

function addPipes(state: BuildState, side: WallSide, along: number, length: number, y: number): void {
  const mount = wallMount(state, side, along, y);
  for (const [offset, radius, color] of [
    [0, 0.09, 0x4a5262],
    [0.26, 0.06, 0x6b5540],
    [-0.24, 0.05, 0x3c4a55],
  ] as const) {
    const pipe = new THREE.Mesh(
      new THREE.CylinderGeometry(radius, radius, length, 10),
      surface(color, { metalness: 0.55, roughness: 0.5 }),
    );
    pipe.rotation.z = Math.PI / 2;
    pipe.position.set(0, offset, 0.2);
    mount.add(pipe);
  }
}

/* ------------------------------------------------------------------ *
 * Furniture + props
 * ------------------------------------------------------------------ */

function addDesk(
  state: BuildState,
  x: number,
  z: number,
  width: number,
  depth: number,
  color: number,
  topY = 0.78,
): THREE.Group {
  const group = new THREE.Group();
  const frameMaterial = surface(0x232833, { roughness: 0.6, metalness: 0.36 });

  const top = new THREE.Mesh(new THREE.BoxGeometry(width, 0.09, depth), surface(color, { roughness: 0.66 }));
  top.position.y = topY;
  top.castShadow = true;
  top.receiveShadow = true;
  group.add(top);

  const lip = new THREE.Mesh(new THREE.BoxGeometry(width, 0.035, 0.04), surface(0x161b25));
  lip.position.set(0, topY - 0.05, depth / 2);
  group.add(lip);

  const legInsetX = Math.max(0.2, width / 2 - 0.26);
  const legInsetZ = Math.max(0.16, depth / 2 - 0.18);
  for (const lx of [-legInsetX, legInsetX]) {
    for (const lz of [-legInsetZ, legInsetZ]) {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(0.08, topY, 0.08), frameMaterial);
      leg.position.set(lx, topY / 2, lz);
      leg.castShadow = true;
      group.add(leg);
    }
  }

  const brace = new THREE.Mesh(
    new THREE.BoxGeometry(Math.max(0.4, width - 0.44), 0.05, 0.05),
    frameMaterial,
  );
  brace.position.set(0, topY * 0.4, -depth / 2 + 0.14);
  group.add(brace);

  group.position.set(x, 0, z);
  state.scene.add(group);
  state.colliders.push({
    minX: x - width / 2,
    maxX: x + width / 2,
    minZ: z - depth / 2,
    maxZ: z + depth / 2,
  });
  return group;
}

function addChair(state: BuildState, x: number, z: number, color: number): THREE.Group {
  const chair = new THREE.Group();
  const material = surface(color, { roughness: 0.85 });

  const seat = new THREE.Mesh(new THREE.BoxGeometry(0.56, 0.1, 0.56), material);
  seat.position.y = 0.5;
  seat.castShadow = true;
  chair.add(seat);

  const cushion = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.06, 0.5), surface(0x1d222d, { roughness: 0.95 }));
  cushion.position.y = 0.56;
  chair.add(cushion);

  const back = new THREE.Mesh(new THREE.BoxGeometry(0.54, 0.74, 0.09), material);
  back.position.set(0, 0.92, 0.27);
  back.rotation.x = -0.1;
  back.castShadow = true;
  chair.add(back);

  for (const side of [-1, 1]) {
    const arm = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.05, 0.36), surface(0x161a22));
    arm.position.set(side * 0.31, 0.68, 0.06);
    chair.add(arm);
    const armPost = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.18, 0.05), surface(0x161a22));
    armPost.position.set(side * 0.31, 0.58, 0.2);
    chair.add(armPost);
  }

  const stem = new THREE.Mesh(
    cachedGeometry("chairStem", () => new THREE.CylinderGeometry(0.045, 0.055, 0.45, 10)),
    surface(0x1a1d23, { metalness: 0.3 }),
  );
  stem.position.y = 0.26;
  chair.add(stem);

  for (let index = 0; index < 5; index += 1) {
    const angle = (index / 5) * Math.PI * 2;
    const spoke = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.035, 0.05), surface(0x151920, { metalness: 0.2 }));
    spoke.position.set(Math.cos(angle) * 0.17, 0.055, Math.sin(angle) * 0.17);
    spoke.rotation.y = -angle;
    chair.add(spoke);
    const caster = new THREE.Mesh(
      cachedGeometry("caster", () => new THREE.SphereGeometry(0.045, 8, 6)),
      surface(0x0e1116),
    );
    caster.position.set(Math.cos(angle) * 0.33, 0.045, Math.sin(angle) * 0.33);
    chair.add(caster);
  }

  chair.position.set(x, 0, z);
  state.scene.add(chair);
  return chair;
}

function addBench(state: BuildState, x: number, z: number, width: number): THREE.Group {
  const group = new THREE.Group();
  const seat = new THREE.Mesh(new THREE.BoxGeometry(width, 0.12, 0.62), surface(0x333947, { roughness: 0.82 }));
  seat.position.y = 0.45;
  seat.castShadow = true;
  group.add(seat);
  const back = new THREE.Mesh(new THREE.BoxGeometry(width, 0.34, 0.08), surface(0x333947, { roughness: 0.82 }));
  back.position.set(0, 0.72, -0.26);
  back.rotation.x = 0.16;
  group.add(back);
  for (const side of [-1, 1]) {
    const leg = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.43, 0.5), surface(0x242a34, { metalness: 0.25 }));
    leg.position.set(side * (width / 2 - 0.34), 0.23, 0);
    group.add(leg);
  }
  group.position.set(x, 0, z);
  state.scene.add(group);
  state.colliders.push({ minX: x - width / 2, maxX: x + width / 2, minZ: z - 0.36, maxZ: z + 0.36 });
  return group;
}

function addSofa(state: BuildState, x: number, z: number): THREE.Group {
  const group = new THREE.Group();
  const fabric = surface(0x4b414d, { roughness: 0.97 });
  const base = new THREE.Mesh(new THREE.BoxGeometry(3.2, 0.42, 1.22), fabric);
  base.position.y = 0.27;
  base.castShadow = true;
  group.add(base);
  const back = new THREE.Mesh(new THREE.BoxGeometry(3.2, 0.86, 0.3), fabric);
  back.position.set(0, 0.77, -0.46);
  back.rotation.x = -0.09;
  back.castShadow = true;
  group.add(back);
  for (const side of [-1, 1]) {
    const arm = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.62, 1.22), fabric);
    arm.position.set(side * 1.48, 0.44, 0);
    arm.castShadow = true;
    group.add(arm);
  }
  for (const offset of [-0.74, 0, 0.74]) {
    const cushion = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.16, 0.86), surface(0x62526a, { roughness: 0.99 }));
    cushion.position.set(offset, 0.56, 0.08);
    cushion.castShadow = true;
    group.add(cushion);
  }
  const blanket = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.07, 0.9), surface(0x7a4b52, { roughness: 0.99 }));
  blanket.position.set(1.0, 0.66, 0.1);
  blanket.rotation.z = 0.06;
  group.add(blanket);

  group.position.set(x, 0, z);
  state.scene.add(group);
  state.colliders.push({ minX: x - 1.66, maxX: x + 1.66, minZ: z - 0.68, maxZ: z + 0.68 });
  return group;
}

function addRug(state: BuildState, x: number, z: number, width: number, depth: number, color: number): void {
  // Lit, not unlit: a MeshBasicMaterial rug glowed like a hole in the floor.
  const texture = floorTexture("carpet");
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(width / 2, depth / 2);
  const rug = new THREE.Mesh(
    new THREE.PlaneGeometry(width, depth),
    new THREE.MeshStandardMaterial({ color, map: texture, roughness: 1 }),
  );
  rug.rotation.x = -Math.PI / 2;
  rug.position.set(x, 0.014, z);
  rug.receiveShadow = true;
  state.scene.add(rug);

  const border = new THREE.Mesh(
    new THREE.PlaneGeometry(width - 0.6, depth - 0.6),
    surface(new THREE.Color(color).offsetHSL(0, 0.04, -0.09).getHex(), { roughness: 1 }),
  );
  border.rotation.x = -Math.PI / 2;
  border.position.set(x, 0.018, z);
  border.receiveShadow = true;
  state.scene.add(border);
}

function addCrate(
  state: BuildState,
  x: number,
  z: number,
  width: number,
  depth: number,
  height: number,
): THREE.Group {
  const group = new THREE.Group();
  const body = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), surface(0x5f5240, { roughness: 0.92 }));
  body.position.y = height / 2;
  body.castShadow = true;
  group.add(body);
  const slatMaterial = surface(0x806c50, { roughness: 0.86 });
  for (const side of [-1, 0, 1]) {
    const slat = new THREE.Mesh(new THREE.BoxGeometry(0.09, height * 0.92, depth + 0.04), slatMaterial);
    slat.position.set(side * (width * 0.39), height / 2, 0);
    group.add(slat);
  }
  for (const side of [-1, 1]) {
    const rail = new THREE.Mesh(new THREE.BoxGeometry(width + 0.04, 0.08, 0.09), slatMaterial);
    rail.position.set(0, height * (side > 0 ? 0.88 : 0.14), depth / 2);
    group.add(rail);
  }
  group.position.set(x, 0, z);
  state.scene.add(group);
  state.colliders.push({ minX: x - width / 2, maxX: x + width / 2, minZ: z - depth / 2, maxZ: z + depth / 2 });
  return group;
}

/* ------------------------------------------------------------------ *
 * The backpack
 * ------------------------------------------------------------------ */

function roundedShape(width: number, height: number, radius: number): THREE.Shape {
  const hw = width / 2;
  const hh = height / 2;
  const r = Math.min(radius, hw, hh);
  const shape = new THREE.Shape();
  shape.moveTo(-hw + r, -hh);
  shape.lineTo(hw - r, -hh);
  shape.quadraticCurveTo(hw, -hh, hw, -hh + r);
  shape.lineTo(hw, hh - r);
  shape.quadraticCurveTo(hw, hh, hw - r, hh);
  shape.lineTo(-hw + r, hh);
  shape.quadraticCurveTo(-hw, hh, -hw, hh - r);
  shape.lineTo(-hw, -hh + r);
  shape.quadraticCurveTo(-hw, -hh, -hw + r, -hh);
  return shape;
}

function softSlab(
  key: string,
  width: number,
  height: number,
  depth: number,
  radius: number,
  bevel = 0.035,
): THREE.BufferGeometry {
  return cachedGeometry(`slab|${key}`, () => {
    const geometry = new THREE.ExtrudeGeometry(roundedShape(width, height, radius), {
      depth,
      bevelEnabled: true,
      bevelThickness: bevel,
      bevelSize: bevel,
      bevelSegments: 3,
      curveSegments: 10,
    });
    geometry.center();
    return geometry;
  });
}

function tube(points: [number, number, number][], radius: number, key: string): THREE.BufferGeometry {
  return cachedGeometry(`tube|${key}`, () => {
    const curve = new THREE.CatmullRomCurve3(points.map(([x, y, z]) => new THREE.Vector3(x, y, z)));
    return new THREE.TubeGeometry(curve, 24, radius, 8, false);
  });
}

/**
 * A recognisable backpack: soft extruded body, lid, front pocket, real shoulder
 * straps that curve down the back, a grab handle, zip lines and side pockets.
 * The previous version was a rotated capsule, which read as a giant pill.
 */
function addBackpack(
  state: BuildState,
  x: number,
  z: number,
  options: {
    baseY?: number;
    rotationY?: number;
    color?: number;
    accent?: number;
    scale?: number;
    collider?: boolean;
    open?: boolean;
  } = {},
): THREE.Group {
  const baseY = options.baseY ?? 0;
  const scale = options.scale ?? 1;
  const shell = options.color ?? 0x3d6893;
  const accent = options.accent ?? MAT.green;
  const group = new THREE.Group();

  const canvasMaterial = surface(shell, { roughness: 0.96 });
  const panelMaterial = surface(new THREE.Color(shell).offsetHSL(0, -0.04, -0.07).getHex(), {
    roughness: 0.97,
  });
  const webbing = surface(0x161d29, { roughness: 0.9 });
  const hardware = surface(0x9aa4b2, { metalness: 0.72, roughness: 0.35 });

  const body = new THREE.Mesh(softSlab("bagBody", 0.36, 0.5, 0.16, 0.11, 0.045), canvasMaterial);
  body.position.y = 0.3;
  body.castShadow = true;
  body.receiveShadow = true;
  group.add(body);

  // The lid tilts back so the silhouette is never a flat rectangle.
  const lid = new THREE.Mesh(softSlab("bagLid", 0.34, 0.17, 0.15, 0.075, 0.04), panelMaterial);
  lid.position.set(0, 0.555, options.open ? -0.05 : -0.012);
  lid.rotation.x = options.open ? -0.85 : -0.2;
  lid.castShadow = true;
  group.add(lid);

  const pocket = new THREE.Mesh(softSlab("bagPocket", 0.25, 0.19, 0.055, 0.07, 0.03), panelMaterial);
  pocket.position.set(0, 0.21, 0.115);
  pocket.rotation.x = 0.05;
  pocket.castShadow = true;
  group.add(pocket);

  for (const side of [-1, 1]) {
    const sidePocket = new THREE.Mesh(softSlab("bagSide", 0.09, 0.19, 0.11, 0.04, 0.025), webbing);
    sidePocket.position.set(side * 0.17, 0.2, 0.02);
    group.add(sidePocket);
  }

  // A can riding in the side pocket. Entirely fictional, obviously.
  const can = new THREE.Mesh(
    cachedGeometry("bagCan", () => new THREE.CylinderGeometry(0.032, 0.032, 0.13, 12)),
    surface(MAT.blue, { roughness: 0.35, metalness: 0.4 }),
  );
  can.position.set(0.17, 0.33, 0.02);
  can.rotation.z = 0.12;
  group.add(can);

  for (const side of [-1, 1]) {
    const strap = new THREE.Mesh(
      tube(
        [
          [side * 0.08, 0.53, -0.05],
          [side * 0.13, 0.4, -0.14],
          [side * 0.14, 0.2, -0.16],
          [side * 0.09, 0.06, -0.1],
          [side * 0.05, 0.02, -0.02],
        ],
        0.028,
        `strap${side}`,
      ),
      webbing,
    );
    strap.castShadow = true;
    group.add(strap);

    const pad = new THREE.Mesh(softSlab("bagPad", 0.075, 0.18, 0.05, 0.035, 0.02), webbing);
    pad.position.set(side * 0.135, 0.33, -0.155);
    group.add(pad);

    const buckle = new THREE.Mesh(new THREE.BoxGeometry(0.045, 0.03, 0.02), hardware);
    buckle.position.set(side * 0.115, 0.14, -0.13);
    group.add(buckle);
  }

  const handle = new THREE.Mesh(
    tube(
      [
        [-0.07, 0.53, -0.03],
        [-0.05, 0.6, -0.03],
        [0.05, 0.6, -0.03],
        [0.07, 0.53, -0.03],
      ],
      0.018,
      "bagHandle",
    ),
    webbing,
  );
  group.add(handle);

  const zip = new THREE.Mesh(
    tube(
      [
        [-0.15, 0.29, 0.145],
        [0, 0.315, 0.15],
        [0.15, 0.29, 0.145],
      ],
      0.008,
      "bagZip",
    ),
    hardware,
  );
  group.add(zip);

  const pull = new THREE.Mesh(new THREE.BoxGeometry(0.018, 0.05, 0.008), hardware);
  pull.position.set(0.12, 0.26, 0.152);
  group.add(pull);

  const patch = new THREE.Mesh(
    new THREE.PlaneGeometry(0.15, 0.05),
    new THREE.MeshBasicMaterial({
      map: signTexture(["PACKET_PAT"], accent, { ratio: 3, background: 0x0d131d }),
      toneMapped: false,
    }),
  );
  patch.position.set(0, 0.22, 0.152);
  group.add(patch);

  const reflector = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.012, 0.01), unlit(accent, { opacity: 0.85 }));
  reflector.position.set(0, 0.42, 0.09);
  group.add(reflector);

  for (const side of [-1, 1]) {
    const foot = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.035, 0.13), webbing);
    foot.position.set(side * 0.1, 0.02, 0);
    group.add(foot);
  }

  group.position.set(x, baseY, z);
  group.rotation.y = options.rotationY ?? 0;
  group.scale.setScalar(scale);
  state.scene.add(group);

  if ((options.collider ?? true) && baseY < 0.4) {
    const half = 0.32 * scale;
    state.colliders.push({ minX: x - half, maxX: x + half, minZ: z - half, maxZ: z + half });
  }
  return group;
}

/* ------------------------------------------------------------------ *
 * Computers + displays
 * ------------------------------------------------------------------ */

function addPC(
  state: BuildState,
  x: number,
  z: number,
  color: number,
  collider: boolean,
  baseY = 0,
): THREE.Group {
  const group = new THREE.Group();
  const caseWidth = 0.56;
  const caseHeight = 0.96;
  const caseDepth = 0.84;
  const shell = surface(color, { roughness: 0.74, metalness: 0.08 });
  const front = new THREE.Color(color).offsetHSL(-0.01, -0.05, -0.09).getHex();

  const tower = new THREE.Mesh(new THREE.BoxGeometry(caseWidth, caseHeight, caseDepth), shell);
  tower.position.y = caseHeight / 2 + 0.045;
  tower.castShadow = true;
  tower.receiveShadow = true;
  group.add(tower);

  const facia = new THREE.Mesh(
    new THREE.BoxGeometry(caseWidth * 0.92, caseHeight * 0.94, 0.04),
    surface(front, { roughness: 0.76 }),
  );
  facia.position.set(0, caseHeight * 0.52, caseDepth / 2 + 0.02);
  group.add(facia);

  const grille = new THREE.Mesh(
    new THREE.PlaneGeometry(caseWidth * 0.6, caseHeight * 0.3),
    new THREE.MeshBasicMaterial({
      map: canvasTexture("pcGrille", 128, 128, (ctx, w, h) => {
        ctx.fillStyle = "#3a3b33";
        ctx.fillRect(0, 0, w, h);
        ctx.fillStyle = "#20211b";
        for (let gy = 6; gy < h; gy += 12) {
          for (let gx = 6; gx < w; gx += 12) {
            ctx.beginPath();
            ctx.arc(gx, gy, 3.4, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }),
      toneMapped: false,
    }),
  );
  grille.position.set(0, 0.3, caseDepth / 2 + 0.042);
  group.add(grille);

  [0.78, 0.67].forEach((height, index) => {
    const bay = new THREE.Mesh(
      new THREE.BoxGeometry(0.4, 0.08, 0.035),
      surface(index ? 0x3a3b33 : 0x272821, { roughness: 0.6 }),
    );
    bay.position.set(0, height, caseDepth / 2 + 0.044);
    group.add(bay);
  });

  const floppy = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.06, 0.03), surface(0x4f4d42, { roughness: 0.7 }));
  floppy.position.set(-0.035, 0.545, caseDepth / 2 + 0.045);
  group.add(floppy);

  const power = new THREE.Mesh(
    cachedGeometry("pcButton", () => new THREE.CylinderGeometry(0.045, 0.045, 0.02, 16)),
    surface(0x4d5048, { metalness: 0.4, roughness: 0.4 }),
  );
  power.rotation.x = Math.PI / 2;
  power.position.set(-0.135, 0.43, caseDepth / 2 + 0.05);
  group.add(power);

  const led = new THREE.Mesh(
    cachedGeometry("pcLed", () => new THREE.SphereGeometry(0.022, 10, 8)),
    unlit(MAT.green),
  );
  led.position.set(0.16, 0.43, caseDepth / 2 + 0.05);
  led.userData.pulse = true;
  group.add(led);
  const ledGlow = glowSprite(MAT.green, 0.3, 0.45);
  ledGlow.position.copy(led.position);
  group.add(ledGlow);

  const badge = new THREE.Mesh(
    new THREE.PlaneGeometry(0.26, 0.07),
    new THREE.MeshBasicMaterial({
      map: signTexture(["GISÈLE · P4"], 0x3d3b32, { ratio: 4, background: 0xb8ad89 }),
      toneMapped: false,
    }),
  );
  badge.position.set(0.07, 0.46, caseDepth / 2 + 0.046);
  group.add(badge);

  for (const side of [-1, 1]) {
    const foot = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.05, 0.2), surface(0x282821, { roughness: 0.9 }));
    foot.position.set(side * 0.17, 0.026, 0);
    group.add(foot);
  }

  // The famously decorative carry handle.
  const handleMaterial = surface(0x716c59, { roughness: 0.62, metalness: 0.2 });
  for (const zOffset of [-0.18, 0.18]) {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.1, 0.06), handleMaterial);
    post.position.set(0, caseHeight + 0.095, zOffset);
    group.add(post);
  }
  const handle = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.055, 0.42), handleMaterial);
  handle.position.set(0, caseHeight + 0.15, 0);
  handle.rotation.x = -0.04;
  group.add(handle);

  group.position.set(x, baseY, z);
  state.scene.add(group);
  if (collider) {
    state.colliders.push({
      minX: x - caseWidth / 2 - 0.05,
      maxX: x + caseWidth / 2 + 0.05,
      minZ: z - caseDepth / 2 - 0.05,
      maxZ: z + caseDepth / 2 + 0.05,
    });
  }
  return group;
}

function addMonitor(
  state: BuildState,
  x: number,
  z: number,
  rotationY: number,
  content: ScreenContent,
  baseY = 0.78,
  scale = 1,
): THREE.Group {
  const group = new THREE.Group();
  const frame = new THREE.Mesh(
    cachedGeometry("monitorFrame", () => new THREE.BoxGeometry(1.3, 0.76, 0.07)),
    surface(0x10141c, { roughness: 0.45, metalness: 0.15 }),
  );
  frame.position.y = 0.58;
  frame.castShadow = true;
  group.add(frame);

  const screen = screenPanel(1.16, 0.63, content);
  screen.position.set(0, 0.59, 0.038);
  group.add(screen);

  const bloom = glowSprite(content.accent, 1.9, 0.14);
  bloom.position.set(0, 0.59, 0.14);
  group.add(bloom);

  const stand = new THREE.Mesh(
    cachedGeometry("monitorStand", () => new THREE.BoxGeometry(0.1, 0.32, 0.1)),
    surface(0x181c25, { metalness: 0.2 }),
  );
  stand.position.y = 0.17;
  group.add(stand);

  const foot = new THREE.Mesh(
    cachedGeometry("monitorFoot", () => new THREE.CylinderGeometry(0.26, 0.31, 0.045, 18)),
    surface(0x161a22, { metalness: 0.2 }),
  );
  foot.position.y = 0.025;
  group.add(foot);

  group.position.set(x, baseY, z);
  group.rotation.y = rotationY;
  group.scale.setScalar(scale);
  state.scene.add(group);
  return group;
}

function addKeyboard(state: BuildState, x: number, z: number, baseY: number, glow = MAT.purple): THREE.Group {
  const group = new THREE.Group();
  const body = new THREE.Mesh(new THREE.BoxGeometry(1.06, 0.06, 0.4), surface(0x11141c, { roughness: 0.55 }));
  body.rotation.x = -0.05;
  body.castShadow = true;
  group.add(body);

  const keys = new THREE.Mesh(
    new THREE.PlaneGeometry(0.98, 0.34),
    new THREE.MeshBasicMaterial({
      map: canvasTexture(`keys|${glow}`, 256, 96, (ctx, w, h) => {
        ctx.fillStyle = "#1b202a";
        ctx.fillRect(0, 0, w, h);
        for (let row = 0; row < 4; row += 1) {
          const columns = row === 3 ? 8 : 14;
          for (let column = 0; column < columns; column += 1) {
            const kw = w / columns - 3;
            ctx.fillStyle = row === 3 && column === 4 ? rgba(glow, 0.5) : "#2b323d";
            ctx.fillRect(column * (w / columns) + 1.5, row * (h / 4) + 2, kw, h / 4 - 4);
          }
        }
        ctx.fillStyle = rgba(glow, 0.22);
        ctx.fillRect(0, 0, w, h);
      }),
      toneMapped: false,
    }),
  );
  keys.rotation.x = -Math.PI / 2 - 0.05;
  keys.position.y = 0.032;
  group.add(keys);

  const underglow = glowSprite(glow, 1.3, 0.2);
  underglow.position.y = -0.01;
  group.add(underglow);

  group.position.set(x, baseY, z);
  state.scene.add(group);
  return group;
}

function addMouseAndPad(state: BuildState, x: number, z: number, baseY: number): void {
  const pad = new THREE.Mesh(new THREE.PlaneGeometry(0.44, 0.34), unlit(0x11151f, { opacity: 0.95 }));
  pad.rotation.x = -Math.PI / 2;
  pad.position.set(x, baseY + 0.002, z);
  state.scene.add(pad);
  const mouse = new THREE.Mesh(
    cachedGeometry("mouse", () => new THREE.SphereGeometry(0.06, 10, 8)),
    surface(0x1b202b, { roughness: 0.5 }),
  );
  mouse.scale.set(0.75, 0.5, 1.2);
  mouse.position.set(x, baseY + 0.03, z);
  state.scene.add(mouse);
}

/* ------------------------------------------------------------------ *
 * People
 * ------------------------------------------------------------------ */

const SKIN_TONES = [0xdca078, 0xb97a52, 0x8a5a3c, 0xecc6a4, 0x6f452e];
const HAIR_TONES = [0x33231d, 0x1b1712, 0x5a3a22, 0x8d7a52, 0x2b2f3a];

interface PersonOptions {
  collider?: boolean;
  icon?: string;
  hat?: "cap" | "headset" | "none";
  skin?: number;
  hair?: number;
  scale?: number;
  rotationY?: number;
}

function buildPerson(shirt: number, options: PersonOptions = {}): THREE.Group {
  const group = new THREE.Group();
  const shirtMaterial = surface(shirt, { roughness: 0.86 });
  const skinMaterial = surface(options.skin ?? SKIN_TONES[0], { roughness: 0.9 });
  const pantsMaterial = surface(0x1e2532, { roughness: 0.9 });

  for (const side of [-1, 1]) {
    const leg = new THREE.Mesh(
      cachedGeometry("leg", () => new THREE.CapsuleGeometry(0.115, 0.48, 4, 8)),
      pantsMaterial,
    );
    leg.position.set(side * 0.16, 0.42, 0);
    leg.castShadow = true;
    group.add(leg);

    const shoe = new THREE.Mesh(
      cachedGeometry("shoe", () => new THREE.BoxGeometry(0.25, 0.13, 0.39)),
      surface(0x11151c, { roughness: 0.72 }),
    );
    shoe.position.set(side * 0.16, 0.1, 0.07);
    shoe.castShadow = true;
    group.add(shoe);
  }

  const torso = new THREE.Mesh(
    cachedGeometry("torso", () => new THREE.CapsuleGeometry(0.31, 0.56, 6, 12)),
    shirtMaterial,
  );
  torso.position.y = 1.08;
  torso.scale.set(1, 1, 0.82);
  torso.castShadow = true;
  group.add(torso);

  // A soft hoodie shoulder line reads better than a bare capsule.
  const shoulders = new THREE.Mesh(
    cachedGeometry("shoulders", () => new THREE.SphereGeometry(0.33, 14, 8, 0, Math.PI * 2, 0, Math.PI * 0.55)),
    shirtMaterial,
  );
  shoulders.position.y = 1.28;
  shoulders.scale.set(0.96, 0.5, 0.8);
  group.add(shoulders);

  for (const side of [-1, 1]) {
    const arm = new THREE.Mesh(
      cachedGeometry("arm", () => new THREE.CapsuleGeometry(0.088, 0.5, 4, 8)),
      shirtMaterial,
    );
    arm.position.set(side * 0.355, 1.1, 0.02);
    arm.rotation.z = side * -0.11;
    arm.castShadow = true;
    group.add(arm);

    const hand = new THREE.Mesh(
      cachedGeometry("hand", () => new THREE.SphereGeometry(0.07, 10, 8)),
      skinMaterial,
    );
    hand.position.set(side * 0.39, 0.82, 0.05);
    group.add(hand);
  }

  const neck = new THREE.Mesh(
    cachedGeometry("neck", () => new THREE.CylinderGeometry(0.08, 0.09, 0.12, 10)),
    skinMaterial,
  );
  neck.position.y = 1.52;
  group.add(neck);

  const head = new THREE.Mesh(
    cachedGeometry("head", () => new THREE.SphereGeometry(0.225, 18, 14)),
    skinMaterial,
  );
  head.position.y = 1.72;
  head.scale.set(1, 1.08, 0.94);
  head.castShadow = true;
  group.add(head);

  const hair = new THREE.Mesh(
    cachedGeometry("hair", () =>
      new THREE.SphereGeometry(0.235, 16, 10, 0, Math.PI * 2, 0, Math.PI * 0.52),
    ),
    surface(options.hair ?? HAIR_TONES[0], { roughness: 0.96 }),
  );
  hair.position.set(0, 1.775, -0.008);
  hair.rotation.x = -0.12;
  group.add(hair);

  for (const side of [-1, 1]) {
    const eye = new THREE.Mesh(
      cachedGeometry("eye", () => new THREE.SphereGeometry(0.019, 8, 6)),
      unlit(0x141720),
    );
    eye.position.set(side * 0.075, 1.74, 0.205);
    group.add(eye);
  }

  if (options.hat === "cap") {
    const cap = new THREE.Mesh(
      cachedGeometry("cap", () =>
        new THREE.SphereGeometry(0.24, 14, 8, 0, Math.PI * 2, 0, Math.PI * 0.5),
      ),
      surface(shirt, { roughness: 0.8 }),
    );
    cap.position.y = 1.8;
    group.add(cap);
    const brim = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.025, 0.18), surface(shirt, { roughness: 0.8 }));
    brim.position.set(0, 1.79, 0.2);
    group.add(brim);
  } else if (options.hat === "headset") {
    const band = new THREE.Mesh(
      cachedGeometry("headsetBand", () => new THREE.TorusGeometry(0.24, 0.022, 8, 20, Math.PI)),
      surface(0x171c26, { roughness: 0.5 }),
    );
    band.position.y = 1.76;
    band.rotation.y = Math.PI / 2;
    group.add(band);
    for (const side of [-1, 1]) {
      const cup = new THREE.Mesh(
        cachedGeometry("headsetCup", () => new THREE.CylinderGeometry(0.075, 0.075, 0.05, 12)),
        surface(0x171c26, { roughness: 0.5 }),
      );
      cup.rotation.z = Math.PI / 2;
      cup.position.set(side * 0.235, 1.73, 0);
      group.add(cup);
      const ring = new THREE.Mesh(
        cachedGeometry("headsetRing", () => new THREE.TorusGeometry(0.055, 0.012, 6, 16)),
        unlit(MAT.teal, { opacity: 0.8 }),
      );
      ring.rotation.y = Math.PI / 2;
      ring.position.set(side * 0.262, 1.73, 0);
      group.add(ring);
    }
  }

  if (options.icon) {
    const badge = iconSprite(options.icon, 0.32);
    badge.position.set(0, 1.2, 0.32);
    group.add(badge);
  }

  group.scale.setScalar(options.scale ?? 1);
  group.rotation.y = options.rotationY ?? 0;
  return group;
}

function addNPC(
  state: BuildState,
  name: string,
  x: number,
  z: number,
  shirt: number,
  icon: string,
  collider = true,
  options: PersonOptions = {},
): THREE.Group {
  const group = buildPerson(shirt, { icon, ...options });
  const nameTag = labelSprite(name, shirt, Math.max(0.62, name.length * 0.048), 0.17);
  nameTag.position.y = 2.08;
  group.add(nameTag);
  group.position.set(x, 0, z);
  group.userData.bob = x * 0.1 + z;
  state.scene.add(group);
  if (collider) {
    state.colliders.push({ minX: x - 0.44, maxX: x + 0.44, minZ: z - 0.36, maxZ: z + 0.36 });
  }
  return group;
}

/** Background crowd: cheap, seated, and permanently slouched. */
function addSeatedGamer(
  state: BuildState,
  x: number,
  z: number,
  rotationY: number,
  shirt: number,
  seed: number,
): THREE.Group {
  const random = seeded(seed);
  const group = new THREE.Group();
  const shirtMaterial = surface(shirt, { roughness: 0.88 });
  const skin = SKIN_TONES[Math.floor(random() * SKIN_TONES.length)];
  const skinMaterial = surface(skin, { roughness: 0.9 });

  const torso = new THREE.Mesh(
    cachedGeometry("seatTorso", () => new THREE.CapsuleGeometry(0.29, 0.42, 5, 10)),
    shirtMaterial,
  );
  torso.position.y = 0.95;
  torso.scale.set(1, 1, 0.8);
  torso.rotation.x = 0.18;
  torso.castShadow = true;
  group.add(torso);

  const thighs = new THREE.Mesh(new THREE.BoxGeometry(0.44, 0.16, 0.5), surface(0x1e2532, { roughness: 0.9 }));
  thighs.position.set(0, 0.6, 0.24);
  group.add(thighs);

  for (const side of [-1, 1]) {
    const shin = new THREE.Mesh(
      cachedGeometry("shin", () => new THREE.CapsuleGeometry(0.09, 0.34, 4, 8)),
      surface(0x1e2532, { roughness: 0.9 }),
    );
    shin.position.set(side * 0.14, 0.32, 0.46);
    group.add(shin);

    const arm = new THREE.Mesh(
      cachedGeometry("seatArm", () => new THREE.CapsuleGeometry(0.075, 0.42, 4, 8)),
      shirtMaterial,
    );
    arm.position.set(side * 0.33, 0.98, 0.24);
    arm.rotation.x = 1.05;
    group.add(arm);
  }

  const head = new THREE.Mesh(
    cachedGeometry("head", () => new THREE.SphereGeometry(0.225, 18, 14)),
    skinMaterial,
  );
  head.position.set(0, 1.36, 0.06);
  head.scale.set(1, 1.06, 0.94);
  head.castShadow = true;
  group.add(head);

  const hair = new THREE.Mesh(
    cachedGeometry("hair", () =>
      new THREE.SphereGeometry(0.235, 16, 10, 0, Math.PI * 2, 0, Math.PI * 0.52),
    ),
    surface(HAIR_TONES[Math.floor(random() * HAIR_TONES.length)], { roughness: 0.96 }),
  );
  hair.position.set(0, 1.42, 0.05);
  group.add(hair);

  if (random() > 0.45) {
    const band = new THREE.Mesh(
      cachedGeometry("headsetBand", () => new THREE.TorusGeometry(0.24, 0.022, 8, 20, Math.PI)),
      surface(0x171c26, { roughness: 0.5 }),
    );
    band.position.set(0, 1.4, 0.06);
    band.rotation.y = Math.PI / 2;
    group.add(band);
    for (const side of [-1, 1]) {
      const cup = new THREE.Mesh(
        cachedGeometry("headsetCup", () => new THREE.CylinderGeometry(0.075, 0.075, 0.05, 12)),
        surface(0x171c26, { roughness: 0.5 }),
      );
      cup.rotation.z = Math.PI / 2;
      cup.position.set(side * 0.235, 1.37, 0.06);
      group.add(cup);
    }
  }

  group.position.set(x, 0, z);
  group.rotation.y = rotationY;
  // A slow lean keeps the crowd from looking like mannequins.
  group.userData.bob = seed * 0.7;
  state.scene.add(group);
  state.colliders.push({ minX: x - 0.42, maxX: x + 0.42, minZ: z - 0.42, maxZ: z + 0.42 });
  return group;
}

/* ------------------------------------------------------------------ *
 * Devices, signage and set dressing
 * ------------------------------------------------------------------ */

function addKiosk(
  state: BuildState,
  x: number,
  z: number,
  content: ScreenContent,
  scale = 1,
  rotationY = 0,
): THREE.Group {
  const group = new THREE.Group();
  const body = new THREE.Mesh(
    cachedGeometry("kioskBody", () => new THREE.BoxGeometry(1.05, 1.72, 0.68)),
    surface(0x39404d, { roughness: 0.52, metalness: 0.2 }),
  );
  body.position.y = 0.86;
  body.castShadow = true;
  group.add(body);

  const hood = new THREE.Mesh(new THREE.BoxGeometry(1.12, 0.12, 0.82), surface(0x252b36, { metalness: 0.3 }));
  hood.position.set(0, 1.72, 0.06);
  group.add(hood);

  const bezel = new THREE.Mesh(new THREE.BoxGeometry(0.84, 0.64, 0.06), surface(0x11151d, { roughness: 0.4 }));
  bezel.position.set(0, 1.16, 0.345);
  bezel.rotation.x = -0.14;
  group.add(bezel);

  const screen = screenPanel(0.74, 0.54, content);
  screen.position.set(0, 1.164, 0.382);
  screen.rotation.x = -0.14;
  group.add(screen);

  const bloom = glowSprite(content.accent, 1.5, 0.22);
  bloom.position.set(0, 1.16, 0.5);
  group.add(bloom);

  const keypad = new THREE.Mesh(new THREE.BoxGeometry(0.46, 0.24, 0.08), surface(0x212832, { metalness: 0.22 }));
  keypad.position.set(0, 0.72, 0.36);
  keypad.rotation.x = -0.32;
  group.add(keypad);

  for (let row = 0; row < 3; row += 1) {
    for (let column = 0; column < 3; column += 1) {
      const key = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.05, 0.02), surface(0x39414d));
      key.position.set(-0.14 + column * 0.14, 0.79 - row * 0.06, 0.4 - row * 0.018);
      key.rotation.x = -0.32;
      group.add(key);
    }
  }

  const slot = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.03, 0.03), unlit(0x05080d));
  slot.position.set(0, 0.55, 0.35);
  group.add(slot);

  const foot = new THREE.Mesh(new THREE.BoxGeometry(1.16, 0.08, 0.82), surface(0x262d37, { metalness: 0.28 }));
  foot.position.y = 0.04;
  group.add(foot);

  group.position.set(x, 0, z);
  group.rotation.y = rotationY;
  group.scale.setScalar(scale);
  state.scene.add(group);
  state.colliders.push({
    minX: x - 0.6 * scale,
    maxX: x + 0.6 * scale,
    minZ: z - 0.44 * scale,
    maxZ: z + 0.44 * scale,
  });
  return group;
}

function addLoreTerminal(
  state: BuildState,
  x: number,
  z: number,
  title: string,
  lines: string[],
  rotationY = 0,
): THREE.Group {
  const terminal = addKiosk(
    state,
    x,
    z,
    { title, lines, accent: MAT.purple, footer: "archive · lecture libre" },
    0.8,
    rotationY,
  );
  const beacon = glowSprite(MAT.purple, 1.8, 0.28);
  beacon.position.set(0, 2.3, 0);
  beacon.userData.pulse = true;
  terminal.add(beacon);
  const tape = new THREE.Mesh(
    cachedGeometry("archiveTape", () => new THREE.TorusGeometry(0.16, 0.05, 8, 20)),
    unlit(MAT.purple, { opacity: 0.75 }),
  );
  tape.position.set(0, 2.15, 0);
  tape.rotation.x = Math.PI / 2;
  tape.userData.spin = 0.9;
  terminal.add(tape);
  return terminal;
}

function addSecurityDoor(
  state: BuildState,
  x: number,
  z: number,
  color: number,
  rotationY = 0,
  closed = false,
  label = closed ? "SORTIE" : "ACCÈS",
): THREE.Group {
  const group = new THREE.Group();
  const archMaterial = surface(0x2f3849, { roughness: 0.52, metalness: 0.42 });

  for (const px of [-1.15, 1.15]) {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.22, 2.55, 0.32), archMaterial);
    post.position.set(px, 1.275, 0);
    post.castShadow = true;
    group.add(post);
    const strip = new THREE.Mesh(new THREE.BoxGeometry(0.05, 2.1, 0.04), unlit(color, { opacity: 0.85 }));
    strip.position.set(px + (px < 0 ? 0.13 : -0.13), 1.25, 0.17);
    group.add(strip);
  }

  const top = new THREE.Mesh(new THREE.BoxGeometry(2.52, 0.23, 0.32), archMaterial);
  top.position.y = 2.44;
  top.castShadow = true;
  group.add(top);

  if (closed) {
    group.userData.portalType = "sealed-exit";
    const panel = new THREE.Mesh(
      new THREE.BoxGeometry(2.04, 2.2, 0.12),
      surface(0x151d2a, { roughness: 0.56, metalness: 0.4, emissive: color, emissiveIntensity: 0.08 }),
    );
    panel.name = "sealed-exit-panel";
    panel.position.set(0, 1.1, 0.02);
    panel.castShadow = true;
    panel.receiveShadow = true;
    group.add(panel);

    const accentMaterial = unlit(color);
    const seam = new THREE.Mesh(new THREE.BoxGeometry(0.035, 1.94, 0.135), accentMaterial);
    seam.position.set(0, 1.1, -0.045);
    group.add(seam);

    for (const px of [-0.84, 0.84]) {
      const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.045, 1.82, 0.14), accentMaterial);
      stripe.position.set(px, 1.1, -0.05);
      group.add(stripe);
    }

    const chevrons = new THREE.Mesh(
      new THREE.PlaneGeometry(1.9, 0.4),
      new THREE.MeshBasicMaterial({
        map: signTexture([label], color, { ratio: 4.8, background: 0x0d1421 }),
        toneMapped: false,
      }),
    );
    chevrons.position.set(0, 1.72, -0.075);
    chevrons.rotation.y = Math.PI;
    group.add(chevrons);

    const threshold = new THREE.Mesh(
      new THREE.BoxGeometry(2.36, 0.07, 0.44),
      surface(0x3c4759, { roughness: 0.5, metalness: 0.5 }),
    );
    threshold.position.set(0, 0.035, -0.06);
    threshold.receiveShadow = true;
    group.add(threshold);
  }

  const glow = glowSprite(color, 2.6, 0.3);
  glow.position.set(0, 2.1, closed ? -0.32 : 0);
  group.add(glow);

  const tag = labelSprite(label, color, 1.4, 0.35);
  if (closed) tag.name = "sealed-exit-label";
  tag.position.set(0, 2.48, closed ? -0.19 : 0.19);
  group.add(tag);

  group.position.set(x, 0, z);
  group.rotation.y = rotationY;
  state.scene.add(group);
  return group;
}

function addScannerArch(state: BuildState, x: number, z: number): THREE.Group {
  const group = addSecurityDoor(state, x, z, MAT.red, 0, false, "SCAN");
  const scan = new THREE.Mesh(
    new THREE.PlaneGeometry(1.95, 2.05),
    unlit(MAT.red, { opacity: 0.12, additive: true, side: THREE.DoubleSide }),
  );
  scan.position.y = 1.25;
  scan.userData.pulse = true;
  group.add(scan);

  const beam = new THREE.Mesh(new THREE.BoxGeometry(1.95, 0.03, 0.06), unlit(MAT.red, { opacity: 0.9 }));
  beam.position.y = 1.2;
  group.add(beam);
  state.updaters.push((elapsed) => {
    beam.position.y = 1.2 + Math.sin(elapsed * 1.6) * 0.85;
  });
  return group;
}

function addBarrierQueue(
  state: BuildState,
  x: number,
  z: number,
  width: number,
  color = MAT.red,
): void {
  const material = surface(0x4f5a6c, { metalness: 0.5, roughness: 0.45 });
  for (const px of [x, x + width / 2, x + width]) {
    const pole = new THREE.Mesh(
      cachedGeometry("queuePole", () => new THREE.CylinderGeometry(0.05, 0.05, 1.02, 10)),
      material,
    );
    pole.position.set(px, 0.6, z);
    pole.castShadow = true;
    state.scene.add(pole);

    const base = new THREE.Mesh(
      cachedGeometry("queueBase", () => new THREE.CylinderGeometry(0.2, 0.24, 0.06, 14)),
      surface(0x1d222c, { metalness: 0.4 }),
    );
    base.position.set(px, 0.03, z);
    state.scene.add(base);

    const cap = new THREE.Mesh(
      cachedGeometry("queueCap", () => new THREE.CylinderGeometry(0.07, 0.07, 0.1, 12)),
      surface(0x2b3240, { metalness: 0.5 }),
    );
    cap.position.set(px, 1.14, z);
    state.scene.add(cap);

    state.colliders.push({ minX: px - 0.2, maxX: px + 0.2, minZ: z - 0.2, maxZ: z + 0.2 });
  }
  for (let i = 0; i < 2; i += 1) {
    const strap = new THREE.Mesh(new THREE.BoxGeometry(width / 2, 0.09, 0.02), unlit(color, { opacity: 0.9 }));
    strap.position.set(x + width / 4 + (i * width) / 2, 1.03, z);
    state.scene.add(strap);
    state.colliders.push({
      minX: strap.position.x - width / 4,
      maxX: strap.position.x + width / 4,
      minZ: z - 0.06,
      maxZ: z + 0.06,
    });
  }
}

function addBanner(
  state: BuildState,
  x: number,
  y: number,
  z: number,
  width: number,
  height: number,
  lines: string[],
  accent: number,
  rotationY = 0,
): void {
  const group = new THREE.Group();
  const cloth = new THREE.Mesh(
    new THREE.PlaneGeometry(width, height),
    new THREE.MeshBasicMaterial({
      map: signTexture(lines, accent, { ratio: width / height, background: 0x0e1420 }),
      side: THREE.DoubleSide,
      toneMapped: false,
    }),
  );
  group.add(cloth);
  const rod = new THREE.Mesh(
    new THREE.CylinderGeometry(0.03, 0.03, width + 0.3, 8),
    surface(0x39404e, { metalness: 0.6 }),
  );
  rod.rotation.z = Math.PI / 2;
  rod.position.y = height / 2 + 0.08;
  group.add(rod);
  for (const side of [-1, 1]) {
    const wire = new THREE.Mesh(
      new THREE.CylinderGeometry(0.008, 0.008, 1.2, 6),
      surface(0x2a3140, { metalness: 0.5 }),
    );
    wire.position.set((side * width) / 2.4, height / 2 + 0.68, 0);
    group.add(wire);
  }
  group.position.set(x, y, z);
  group.rotation.y = rotationY;
  state.scene.add(group);
}

function addFloorDecal(
  state: BuildState,
  x: number,
  z: number,
  width: number,
  depth: number,
  lines: string[],
  accent: number,
  rotationZ = 0,
): void {
  const decal = new THREE.Mesh(
    new THREE.PlaneGeometry(width, depth),
    new THREE.MeshBasicMaterial({
      map: signTexture(lines, accent, { ratio: width / depth, background: 0x121a26 }),
      transparent: true,
      opacity: 0.82,
      toneMapped: false,
    }),
  );
  decal.rotation.x = -Math.PI / 2;
  decal.rotation.z = rotationZ;
  decal.position.set(x, 0.022, z);
  state.scene.add(decal);
}

function addCableRun(
  state: BuildState,
  points: [number, number, number][],
  color: number,
  radius = 0.05,
  key = points.join(","),
): void {
  const cable = new THREE.Mesh(tube(points, radius, `run|${key}`), surface(color, { roughness: 0.6 }));
  state.scene.add(cable);
}

function addCableTray(state: BuildState, x: number, z: number, length: number, rotationY = 0): void {
  const group = new THREE.Group();
  const tray = new THREE.Mesh(new THREE.BoxGeometry(length, 0.05, 0.5), surface(0x2a3140, { metalness: 0.45 }));
  group.add(tray);
  for (const [offset, color] of [
    [-0.15, MAT.blue],
    [-0.05, MAT.green],
    [0.05, MAT.amber],
    [0.15, MAT.red],
  ] as const) {
    const wire = new THREE.Mesh(
      new THREE.CylinderGeometry(0.028, 0.028, length, 6),
      surface(color, { roughness: 0.55 }),
    );
    wire.rotation.z = Math.PI / 2;
    wire.position.set(0, 0.055, offset);
    group.add(wire);
  }
  group.position.set(x, 0.03, z);
  group.rotation.y = rotationY;
  state.scene.add(group);
}

function addPowerBar(state: BuildState, x: number, z: number): THREE.Group {
  const group = new THREE.Group();
  const body = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.22, 0.6), surface(0xe4e0d3, { roughness: 0.65 }));
  body.position.y = 0.14;
  body.castShadow = true;
  group.add(body);
  for (let i = -2; i <= 2; i += 1) {
    const socket = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.02, 0.26), unlit(0x2c2d2e));
    socket.position.set(i * 0.48, 0.252, 0);
    group.add(socket);
    for (const side of [-1, 1]) {
      const hole = new THREE.Mesh(new THREE.BoxGeometry(0.035, 0.02, 0.09), unlit(0x0a0b0c));
      hole.position.set(i * 0.48 + side * 0.06, 0.257, 0);
      group.add(hole);
    }
  }
  const indicator = new THREE.Mesh(
    cachedGeometry("barLed", () => new THREE.SphereGeometry(0.03, 8, 6)),
    unlit(MAT.red),
  );
  indicator.position.set(1.16, 0.26, 0);
  indicator.userData.pulse = true;
  group.add(indicator);

  const cord = new THREE.Mesh(
    tube(
      [
        [1.3, 0.12, 0],
        [2.4, 0.08, -0.5],
        [3.4, 0.08, -1.6],
      ],
      0.035,
      "barCord",
    ),
    surface(MAT.orange, { roughness: 0.7 }),
  );
  group.add(cord);

  group.position.set(x, 0, z);
  state.scene.add(group);
  return group;
}

function addNetworkPort(state: BuildState, x: number, z: number): THREE.Group {
  const group = new THREE.Group();
  const plate = new THREE.Mesh(new THREE.BoxGeometry(2.2, 1.5, 0.3), surface(0x2b323e, { metalness: 0.35 }));
  plate.position.y = 0.9;
  plate.castShadow = true;
  group.add(plate);

  const face = screenPanel(1.7, 0.9, {
    title: "patch panel",
    lines: ["!VLAN 404", "port 24 · libre"],
    accent: MAT.green,
    footer: "ne pas débrancher",
  });
  face.position.set(0, 1.02, 0.155);
  group.add(face);

  for (let i = 0; i < 8; i += 1) {
    const port = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.11, 0.06), unlit(0x07110d));
    port.position.set(-0.7 + i * 0.2, 0.42, 0.16);
    group.add(port);
    const led = new THREE.Mesh(
      cachedGeometry("portLed", () => new THREE.SphereGeometry(0.017, 6, 5)),
      unlit(i % 3 === 0 ? MAT.amber : MAT.green),
    );
    led.position.set(-0.7 + i * 0.2, 0.52, 0.17);
    if (i % 2 === 0) led.userData.pulse = true;
    group.add(led);
  }

  const bloom = glowSprite(MAT.green, 2, 0.2);
  bloom.position.set(0, 0.9, 0.5);
  group.add(bloom);

  group.position.set(x, 0, z);
  state.scene.add(group);
  state.colliders.push({ minX: x - 1.1, maxX: x + 1.1, minZ: z - 0.2, maxZ: z + 0.2 });
  return group;
}

/* ------------------------------------------------------------------ *
 * Outdoor props
 * ------------------------------------------------------------------ */

function addParkingBay(state: BuildState, x: number, z: number, occupiedColor?: number): void {
  for (const offset of [-2.35, 2.35]) {
    const line = plane(0.13, 5.4, 0xc9b959, 0.72);
    line.rotation.x = -Math.PI / 2;
    line.position.set(x + offset, 0.02, z);
    state.scene.add(line);
  }
  const stop = new THREE.Mesh(new THREE.BoxGeometry(3.6, 0.14, 0.24), surface(0x6a6d74, { roughness: 0.95 }));
  stop.position.set(x, 0.07, z - 2.3);
  state.scene.add(stop);
  if (occupiedColor !== undefined) addCar(state, x, z, occupiedColor, true, Math.PI / 2);
}

/** Demi-emprise au sol d'une berline : elle est longue selon son axe X local. */
export const CAR_HALF_LENGTH = 2.22;
export const CAR_HALF_WIDTH = 1.02;

function addCar(
  state: BuildState,
  x: number,
  z: number,
  color: number,
  collider: boolean,
  rotationY = 0,
  options: { openTrunk?: boolean } = {},
): THREE.Group {
  const group = new THREE.Group();
  const paint = surface(color, { metalness: 0.45, roughness: 0.35 });
  const glass = surface(0x18293a, { metalness: 0.2, roughness: 0.16 });

  const lower = new THREE.Mesh(new THREE.BoxGeometry(4.25, 0.62, 1.86), paint);
  lower.position.y = 0.58;
  lower.castShadow = true;
  group.add(lower);

  const skirt = new THREE.Mesh(new THREE.BoxGeometry(4.1, 0.2, 1.9), surface(0x1a1d24, { roughness: 0.9 }));
  skirt.position.y = 0.34;
  group.add(skirt);

  const hood = new THREE.Mesh(new THREE.BoxGeometry(1.3, 0.28, 1.72), paint);
  hood.position.set(1.45, 0.94, 0);
  hood.rotation.z = -0.035;
  hood.castShadow = true;
  group.add(hood);

  const trunk = new THREE.Mesh(new THREE.BoxGeometry(0.82, 0.34, 1.72), paint);
  trunk.name = "car-trunk-lid";
  if (options.openTrunk) {
    // Charnière au bord arrière de l'habitacle : le couvercle se relève au lieu
    // de flotter au-dessus du coffre.
    trunk.position.set(-1.42, 1.32, 0);
    trunk.rotation.z = 1.16;
  } else {
    trunk.position.set(-1.68, 0.91, 0);
    trunk.rotation.z = 0.025;
  }
  trunk.castShadow = true;
  group.add(trunk);

  if (options.openTrunk) {
    const bay = new THREE.Mesh(
      new THREE.BoxGeometry(0.86, 0.3, 1.6),
      surface(0x14171d, { roughness: 0.95 }),
    );
    bay.position.set(-1.66, 0.74, 0);
    group.add(bay);

    const glow = glowSprite(0xffd9a0, 1.5, 0.24);
    glow.position.set(-1.66, 1.02, 0);
    group.add(glow);
  }

  const cabin = new THREE.Mesh(new THREE.BoxGeometry(1.85, 0.72, 1.58), glass);
  cabin.position.set(-0.28, 1.25, 0);
  cabin.rotation.z = -0.025;
  cabin.castShadow = true;
  group.add(cabin);

  const roof = new THREE.Mesh(new THREE.BoxGeometry(1.45, 0.1, 1.66), paint);
  roof.position.set(-0.34, 1.64, 0);
  roof.castShadow = true;
  group.add(roof);

  const wheelGeometry = cachedGeometry("wheel", () => new THREE.CylinderGeometry(0.39, 0.39, 0.24, 18));
  const rimGeometry = cachedGeometry("rim", () => new THREE.CylinderGeometry(0.19, 0.19, 0.252, 12));
  for (const wx of [-1.35, 1.35]) {
    for (const wz of [-0.94, 0.94]) {
      const wheel = new THREE.Mesh(wheelGeometry, surface(0x090a0c, { roughness: 0.85 }));
      wheel.rotation.x = Math.PI / 2;
      wheel.position.set(wx, 0.43, wz);
      wheel.castShadow = true;
      group.add(wheel);

      const rim = new THREE.Mesh(rimGeometry, surface(0x8f969f, { metalness: 0.75, roughness: 0.3 }));
      rim.rotation.x = Math.PI / 2;
      rim.position.copy(wheel.position);
      group.add(rim);
    }
  }

  for (const zOffset of [-0.56, 0.56]) {
    const headlight = new THREE.Mesh(
      cachedGeometry("headlight", () => new THREE.CircleGeometry(0.15, 16)),
      unlit(0xfff0bc),
    );
    headlight.rotation.y = Math.PI / 2;
    headlight.position.set(2.132, 0.78, zOffset);
    group.add(headlight);

    const tail = new THREE.Mesh(
      cachedGeometry("taillight", () => new THREE.CircleGeometry(0.12, 16)),
      unlit(0xff4259),
    );
    tail.rotation.y = -Math.PI / 2;
    tail.position.set(-2.132, 0.77, zOffset);
    group.add(tail);
  }

  const bumper = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.16, 1.66), surface(0x252a31, { metalness: 0.32 }));
  bumper.position.set(2.16, 0.44, 0);
  group.add(bumper);

  group.position.set(x, 0, z);
  group.rotation.y = rotationY;
  state.scene.add(group);
  if (collider) {
    const alongX = Math.abs(Math.cos(rotationY)) > 0.5;
    state.colliders.push({
      minX: x - (alongX ? CAR_HALF_LENGTH : CAR_HALF_WIDTH),
      maxX: x + (alongX ? CAR_HALF_LENGTH : CAR_HALF_WIDTH),
      minZ: z - (alongX ? CAR_HALF_WIDTH : CAR_HALF_LENGTH),
      maxZ: z + (alongX ? CAR_HALF_WIDTH : CAR_HALF_LENGTH),
    });
  }
  return group;
}

function addCone(state: BuildState, x: number, z: number): void {
  const cone = new THREE.Group();
  const base = new THREE.Mesh(
    cachedGeometry("coneBase", () => new THREE.BoxGeometry(0.6, 0.08, 0.6)),
    surface(0x282a2f, { roughness: 0.95 }),
  );
  base.position.y = 0.04;
  cone.add(base);
  const top = new THREE.Mesh(
    cachedGeometry("coneTop", () => new THREE.ConeGeometry(0.24, 0.86, 14)),
    surface(0xf07832, { roughness: 0.7 }),
  );
  top.position.y = 0.5;
  top.castShadow = true;
  cone.add(top);
  const band = new THREE.Mesh(
    cachedGeometry("coneBand", () => new THREE.CylinderGeometry(0.15, 0.18, 0.1, 14)),
    unlit(0xf2f4f8, { opacity: 0.9 }),
  );
  band.position.y = 0.6;
  cone.add(band);
  cone.position.set(x, 0, z);
  state.scene.add(cone);
  state.colliders.push({ minX: x - 0.34, maxX: x + 0.34, minZ: z - 0.34, maxZ: z + 0.34 });
}

function addLampPost(state: BuildState, x: number, z: number, lit: boolean): void {
  const pole = new THREE.Mesh(
    cachedGeometry("lampPole", () => new THREE.CylinderGeometry(0.1, 0.16, 7, 8)),
    surface(0x2f343d, { metalness: 0.4, roughness: 0.6 }),
  );
  pole.position.set(x, 3.5, z);
  pole.castShadow = true;
  state.scene.add(pole);

  const arm = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.1, 0.1), surface(0x2f343d, { metalness: 0.4 }));
  arm.position.set(x + 0.5, 6.9, z);
  state.scene.add(arm);

  const head = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.18, 0.44), surface(0x3a4049, { metalness: 0.5 }));
  head.position.set(x + 1, 6.78, z);
  state.scene.add(head);

  const color = lit ? 0xffd9a0 : 0x4a5163;
  const lens = new THREE.Mesh(
    new THREE.PlaneGeometry(0.7, 0.36),
    unlit(color, { opacity: lit ? 0.95 : 0.35 }),
  );
  lens.rotation.x = Math.PI / 2;
  lens.position.set(x + 1, 6.68, z);
  state.scene.add(lens);

  if (lit) {
    const halo = glowSprite(0xffcf90, 7, 0.2);
    halo.position.set(x + 1, 6.4, z);
    state.scene.add(halo);
    addLightShaft(state, x + 1, z, 0xffcf90, 3.2, 6.6, 0.035);
  }
  state.colliders.push({ minX: x - 0.3, maxX: x + 0.3, minZ: z - 0.3, maxZ: z + 0.3 });
}

function addBillboard(state: BuildState, x: number, z: number, lines: string[], rotationY = 0): void {
  const group = new THREE.Group();
  for (const side of [-1, 1]) {
    const pole = new THREE.Mesh(new THREE.BoxGeometry(0.22, 5, 0.22), surface(0x454c58, { metalness: 0.55 }));
    pole.position.set(side * 2.6, 2.5, 0);
    pole.castShadow = true;
    group.add(pole);
  }
  const board = new THREE.Mesh(new THREE.BoxGeometry(9, 4.4, 0.3), surface(0x1c222d, { roughness: 0.8 }));
  board.position.set(0, 5.1, 0);
  board.castShadow = true;
  group.add(board);
  const face = signPanel(8.4, 3.8, lines, MAT.amber, { kicker: "LAN QUÉBEC · ÉDITION 404" });
  face.position.set(0, 5.1, 0.16);
  group.add(face);
  const glow = glowSprite(MAT.amber, 9, 0.12);
  glow.position.set(0, 5.1, 0.5);
  group.add(glow);
  group.position.set(x, 0, z);
  group.rotation.y = rotationY;
  state.scene.add(group);
  state.colliders.push({ minX: x - 2.9, maxX: x + 2.9, minZ: z - 0.4, maxZ: z + 0.4 });
}

function addCart(state: BuildState, x: number, z: number): THREE.Group {
  const group = new THREE.Group();
  const frame = surface(0x78838f, { metalness: 0.8, roughness: 0.3 });
  const basket = new THREE.Mesh(
    new THREE.BoxGeometry(1.1, 0.6, 0.72),
    new THREE.MeshBasicMaterial({ color: 0x8d99ab, wireframe: true, toneMapped: false }),
  );
  basket.position.y = 0.85;
  group.add(basket);
  const floor = new THREE.Mesh(new THREE.BoxGeometry(1.05, 0.04, 0.68), frame);
  floor.position.y = 0.56;
  group.add(floor);
  const handle = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.06, 0.7), frame);
  handle.position.set(-0.55, 1.05, 0);
  group.add(handle);
  for (const wx of [-0.42, 0.42]) {
    for (const wz of [-0.27, 0.27]) {
      const wheel = new THREE.Mesh(
        cachedGeometry("cartWheel", () => new THREE.CylinderGeometry(0.09, 0.09, 0.05, 10)),
        surface(0x15171b),
      );
      wheel.rotation.x = Math.PI / 2;
      wheel.position.set(wx, 0.09, wz);
      group.add(wheel);
      const strut = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.46, 0.04), frame);
      strut.position.set(wx, 0.33, wz);
      group.add(strut);
    }
  }
  group.position.set(x, 0, z);
  state.scene.add(group);
  return group;
}

/** The legendary hand truck. Carrying eighteen kilos is a design problem. */
function addHandTruck(state: BuildState, x: number, z: number, rotationY = 0): THREE.Group {
  const group = new THREE.Group();
  const steel = surface(0xb04a3a, { metalness: 0.45, roughness: 0.5 });
  const dark = surface(0x21262f, { metalness: 0.4, roughness: 0.6 });

  for (const side of [-1, 1]) {
    const rail = new THREE.Mesh(new THREE.BoxGeometry(0.07, 1.3, 0.07), steel);
    rail.position.set(side * 0.21, 0.72, 0);
    rail.rotation.x = -0.1;
    rail.castShadow = true;
    group.add(rail);

    const wheel = new THREE.Mesh(
      cachedGeometry("truckWheel", () => new THREE.CylinderGeometry(0.19, 0.19, 0.09, 14)),
      surface(0x121519, { roughness: 0.85 }),
    );
    wheel.rotation.z = Math.PI / 2;
    wheel.position.set(side * 0.28, 0.19, 0.06);
    group.add(wheel);
  }

  const toe = new THREE.Mesh(new THREE.BoxGeometry(0.52, 0.05, 0.3), steel);
  toe.position.set(0, 0.1, -0.16);
  toe.castShadow = true;
  group.add(toe);

  for (let i = 0; i < 3; i += 1) {
    const rung = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.045, 0.045), steel);
    rung.position.set(0, 0.42 + i * 0.36, 0.045 - i * 0.035);
    group.add(rung);
  }

  const grip = new THREE.Mesh(new THREE.BoxGeometry(0.56, 0.06, 0.06), dark);
  grip.position.set(0, 1.36, -0.06);
  group.add(grip);

  const sticker = new THREE.Mesh(
    new THREE.PlaneGeometry(0.34, 0.11),
    new THREE.MeshBasicMaterial({
      map: signTexture(["LE DIABLE"], MAT.amber, { ratio: 3.2, background: 0x161d29 }),
      toneMapped: false,
    }),
  );
  sticker.position.set(0, 0.98, 0.06);
  sticker.rotation.x = -0.1;
  group.add(sticker);

  group.position.set(x, 0, z);
  group.rotation.y = rotationY;
  state.scene.add(group);
  state.colliders.push({ minX: x - 0.36, maxX: x + 0.36, minZ: z - 0.3, maxZ: z + 0.3 });
  return group;
}

/* ------------------------------------------------------------------ *
 * Indoor set pieces
 * ------------------------------------------------------------------ */

function addVendingMachine(
  state: BuildState,
  x: number,
  z: number,
  rotationY: number,
  content: ScreenContent,
  bodyColor: number,
): THREE.Group {
  const group = new THREE.Group();
  const body = new THREE.Mesh(new THREE.BoxGeometry(1.2, 2.1, 0.78), surface(bodyColor, { roughness: 0.45 }));
  body.position.y = 1.05;
  body.castShadow = true;
  group.add(body);

  const window_ = new THREE.Mesh(new THREE.BoxGeometry(0.9, 1.3, 0.05), surface(0x0b1017, { roughness: 0.2 }));
  window_.position.set(-0.1, 1.32, 0.4);
  group.add(window_);

  for (let row = 0; row < 4; row += 1) {
    for (let column = 0; column < 4; column += 1) {
      const snack = new THREE.Mesh(
        new THREE.BoxGeometry(0.16, 0.2, 0.03),
        unlit([MAT.amber, MAT.red, MAT.green, MAT.blue, MAT.purple][(row + column) % 5], { opacity: 0.9 }),
      );
      snack.position.set(-0.42 + column * 0.21, 1.82 - row * 0.32, 0.43);
      group.add(snack);
    }
  }

  const display = screenPanel(0.44, 0.66, content);
  display.position.set(0.42, 1.42, 0.4);
  group.add(display);

  const tray = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.28, 0.1), unlit(0x080b11));
  tray.position.set(-0.1, 0.44, 0.4);
  group.add(tray);

  const bloom = glowSprite(content.accent, 2.2, 0.2);
  bloom.position.set(0, 1.4, 0.7);
  group.add(bloom);

  group.position.set(x, 0, z);
  group.rotation.y = rotationY;
  state.scene.add(group);
  const alongX = Math.abs(Math.cos(rotationY)) > 0.5;
  state.colliders.push({
    minX: x - (alongX ? 0.65 : 0.45),
    maxX: x + (alongX ? 0.65 : 0.45),
    minZ: z - (alongX ? 0.45 : 0.65),
    maxZ: z + (alongX ? 0.45 : 0.65),
  });
  return group;
}

function addCoffeeMachine(state: BuildState, x: number, z: number, rotationY = 0): THREE.Group {
  const group = addVendingMachine(
    state,
    x,
    z,
    rotationY,
    { title: "café v0.3", lines: ["!BRUN", "chaud-ish"], accent: MAT.amber, ratio: 0.7 },
    0x59413a,
  );
  const steam = glowSprite(0xd9cbb4, 1.1, 0.16);
  steam.position.set(0, 2.4, 0.1);
  steam.userData.bob = 3;
  group.add(steam);
  return group;
}

function addLanStation(
  state: BuildState,
  x: number,
  z: number,
  variant: number,
  options: { crowd?: boolean; seed?: number } = {},
): void {
  const index = Math.abs(variant) % 4;
  const tableColor = [0x34333e, 0x303642, 0x38323d, 0x2e3736][index];
  const accent = [MAT.green, MAT.red, MAT.purple, MAT.amber][index];
  const facing = z < 0 ? 0 : Math.PI;

  addDesk(state, x, z, 5.7, 1.25, tableColor, 0.78);

  const screens: ScreenContent[] = [
    { title: "match", lines: ["!GG", "12 ms"], accent: MAT.green },
    { title: "erreur", lines: ["!404", "seat not found"], accent: MAT.red },
    { title: "queue", lines: ["!LAG", "ping: oui"], accent: MAT.purple },
    { title: "statut", lines: ["!AFK", "retour: jamais"], accent: MAT.amber },
  ];

  for (const offset of [-1.55, 1.45]) {
    addMonitor(state, x + offset, z, facing, screens[(index + (offset > 0 ? 1 : 0)) % 4], 0.78, 0.86);
  }

  const deskFront = z < 0 ? z + 0.5 : z - 0.5;
  addKeyboard(state, x - 0.05, deskFront, 0.83, accent);
  addMouseAndPad(state, x + 0.72, deskFront, 0.835);

  // Under-desk RGB is the whole aesthetic.
  const glowStrip = new THREE.Mesh(new THREE.BoxGeometry(5.4, 0.04, 0.05), unlit(accent, { opacity: 0.75 }));
  glowStrip.position.set(x, 0.68, z + (z < 0 ? 0.6 : -0.6));
  state.scene.add(glowStrip);
  const spill = glowSprite(accent, 5.4, 0.1);
  spill.position.set(x, 0.4, z + (z < 0 ? 0.9 : -0.9));
  state.scene.add(spill);

  const towerColor = [0x1d2431, 0x2a1f2c, 0x1b2b2a, 0x2b2620][index];
  const tower = addPC(state, x + 2.2, z - (z < 0 ? 0.3 : -0.3), towerColor, false, 0);
  tower.scale.setScalar(0.92);

  const chairZ = z + (z < 0 ? 1.35 : -1.35);
  if (options.crowd) {
    addSeatedGamer(state, x + 0.1, chairZ, facing + Math.PI, accent, options.seed ?? Math.round(x * 31 + z * 7));
    const chair = addChair(state, x + 0.1, chairZ + (z < 0 ? 0.18 : -0.18), 0x22262f);
    chair.rotation.y = facing + Math.PI;
  } else {
    const chair = addChair(state, x + 0.9, chairZ, 0x252936);
    chair.rotation.y = facing + Math.PI + 0.4;
  }
}

function addCablePile(parent: THREE.Object3D): void {
  const colors = [MAT.green, MAT.blue, MAT.red, MAT.amber, MAT.teal];
  for (let i = 0; i < 5; i += 1) {
    const coil = new THREE.Mesh(
      cachedGeometry(`coil|${i}`, () => new THREE.TorusGeometry(0.34 + i * 0.07, 0.045, 8, 26)),
      surface(colors[i], { roughness: 0.55 }),
    );
    coil.rotation.x = Math.PI / 2 + i * 0.12;
    coil.rotation.z = i * 0.4;
    coil.position.set((i - 2) * 0.14, 0.78 + i * 0.05, 0);
    parent.add(coil);
  }
  const knot = new THREE.Mesh(
    cachedGeometry("cableKnot", () => new THREE.TorusKnotGeometry(0.22, 0.045, 64, 8, 2, 3)),
    surface(MAT.teal, { roughness: 0.5 }),
  );
  knot.position.set(0.1, 1.06, 0.1);
  knot.userData.spin = 0.25;
  parent.add(knot);
}

function addUSB(state: BuildState, x: number, z: number): THREE.Group {
  const group = new THREE.Group();
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(0.5, 0.14, 0.24),
    surface(MAT.purple, { emissive: MAT.purple, emissiveIntensity: 0.55, roughness: 0.4 }),
  );
  body.position.y = 0.4;
  group.add(body);
  const plug = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.09, 0.19), surface(0xb7bec8, { metalness: 0.8 }));
  plug.position.set(0.32, 0.4, 0);
  group.add(plug);
  const halo = glowSprite(MAT.purple, 1.3, 0.4);
  halo.position.y = 0.4;
  group.add(halo);
  group.position.set(x, 0, z);
  group.userData.bob = 2;
  group.userData.spin = 0.5;
  state.scene.add(group);
  addMarker(group, MAT.purple, 0.55);
  return group;
}

function addRouterBoss(state: BuildState, x: number, z: number): THREE.Group {
  const group = new THREE.Group();

  const core = new THREE.Mesh(
    new THREE.IcosahedronGeometry(2.4, 1),
    surface(0x151f2f, {
      emissive: MAT.red,
      emissiveIntensity: 0.45,
      metalness: 0.7,
      roughness: 0.22,
      flatShading: true,
    }),
  );
  core.position.y = 3.5;
  core.userData.spin = 0.24;
  core.castShadow = true;
  group.add(core);

  const cage = new THREE.Mesh(
    new THREE.IcosahedronGeometry(2.75, 1),
    new THREE.MeshBasicMaterial({
      color: MAT.red,
      wireframe: true,
      transparent: true,
      opacity: 0.28,
      toneMapped: false,
    }),
  );
  cage.position.y = 3.5;
  cage.userData.spin = -0.16;
  group.add(cage);

  for (let i = 0; i < 3; i += 1) {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(3.1 + i * 0.55, 0.07, 8, 48),
      unlit([MAT.red, MAT.purple, MAT.teal][i], { opacity: 0.9 }),
    );
    ring.position.y = 3.5;
    ring.rotation.set(i * 0.7, i * 0.55, i);
    ring.userData.spin = i % 2 ? -0.7 : 0.55;
    group.add(ring);
  }

  // Antennae, because every angry router has too many.
  for (let i = 0; i < 6; i += 1) {
    const angle = (i / 6) * Math.PI * 2;
    const antenna = new THREE.Mesh(
      cachedGeometry("antenna", () => new THREE.CylinderGeometry(0.05, 0.07, 1.8, 8)),
      surface(0x1d242f, { metalness: 0.5 }),
    );
    antenna.position.set(Math.cos(angle) * 1.9, 5.1, Math.sin(angle) * 1.9);
    antenna.rotation.z = Math.cos(angle) * 0.5;
    antenna.rotation.x = -Math.sin(angle) * 0.5;
    group.add(antenna);
    const tip = new THREE.Mesh(
      cachedGeometry("antennaTip", () => new THREE.SphereGeometry(0.08, 8, 6)),
      unlit(MAT.red),
    );
    tip.position.set(Math.cos(angle) * 2.4, 5.9, Math.sin(angle) * 2.4);
    tip.userData.pulse = true;
    group.add(tip);
  }

  const eye = new THREE.Mesh(
    cachedGeometry("bossEye", () => new THREE.SphereGeometry(0.45, 18, 14)),
    unlit(MAT.red),
  );
  eye.position.set(0, 3.5, 2.2);
  eye.userData.pulse = true;
  group.add(eye);
  const eyeGlow = glowSprite(MAT.red, 4, 0.5);
  eyeGlow.position.set(0, 3.5, 2.4);
  group.add(eyeGlow);

  const name = labelSprite("GÉRARD-DHCP", MAT.red, 5.2, 0.78);
  name.position.set(0, 7.1, 0);
  group.add(name);

  group.position.set(x, 0, z);
  group.userData.bob = 1.5;
  state.scene.add(group);
  return group;
}

function addApartmentDoor(state: BuildState, x: number, z: number): THREE.Group {
  const group = new THREE.Group();
  const frameMaterial = surface(0x3a2c29, { roughness: 0.84 });

  // Le battant vit dans un pivot placé sur ses gonds, pour qu'il tourne autour
  // du chambranle au lieu de pivoter sur son propre milieu.
  const pivot = new THREE.Group();
  pivot.name = "door-pivot";
  pivot.position.x = 0.51;
  group.add(pivot);

  const slab = new THREE.Mesh(new THREE.BoxGeometry(1.02, 2.16, 0.09), surface(0x745445, { roughness: 0.88 }));
  slab.position.set(-0.51, 1.08, 0);
  slab.castShadow = true;
  pivot.add(slab);

  for (const yOffset of [0.62, 1.52]) {
    const panel = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.68, 0.02), surface(0x67493c, { roughness: 0.9 }));
    panel.position.set(-0.51, yOffset, 0.055);
    pivot.add(panel);
  }

  for (const side of [-1, 1]) {
    const jamb = new THREE.Mesh(new THREE.BoxGeometry(0.08, 2.28, 0.14), frameMaterial);
    jamb.position.set(side * 0.56, 1.14, 0);
    group.add(jamb);
  }
  const lintel = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.08, 0.14), frameMaterial);
  lintel.position.y = 2.24;
  group.add(lintel);

  const knob = new THREE.Mesh(
    cachedGeometry("knob", () => new THREE.SphereGeometry(0.045, 12, 8)),
    surface(0xb69a57, { roughness: 0.3, metalness: 0.75 }),
  );
  knob.position.set(-0.38, 1.02, 0.075);
  group.add(knob);

  const note = new THREE.Mesh(
    new THREE.PlaneGeometry(0.3, 0.2),
    new THREE.MeshBasicMaterial({
      map: signTexture(["LAN", "19:00"], MAT.red, { ratio: 1.5, background: 0xe8e2cf }),
      toneMapped: false,
    }),
  );
  note.position.set(0.16, 1.5, 0.052);
  note.rotation.z = 0.08;
  group.add(note);

  group.position.set(x, 0, z);
  group.rotation.y = Math.PI / 2;
  state.scene.add(group);
  return group;
}

function addFridge(state: BuildState, x: number, z: number, rotationY = 0): THREE.Group {
  const group = new THREE.Group();
  const shell = surface(0xb9bec7, { roughness: 0.36, metalness: 0.32 });
  const body = new THREE.Mesh(new THREE.BoxGeometry(0.86, 1.82, 0.72), shell);
  body.position.y = 0.91;
  body.castShadow = true;
  group.add(body);

  const freezer = new THREE.Mesh(new THREE.BoxGeometry(0.88, 0.03, 0.74), surface(0x757b85));
  freezer.position.y = 1.32;
  group.add(freezer);

  for (const [y, height] of [
    [1.6, 0.4],
    [0.62, 1.3],
  ] as const) {
    const handle = new THREE.Mesh(new THREE.BoxGeometry(0.05, height, 0.05), surface(0x6d747f, { metalness: 0.6 }));
    handle.position.set(0.3, y, 0.39);
    group.add(handle);
  }

  const magnets = new THREE.Mesh(
    new THREE.PlaneGeometry(0.5, 0.34),
    new THREE.MeshBasicMaterial({
      map: signTexture(["LISTE", "· glace", "· glaçons"], MAT.teal, { ratio: 1.5, background: 0xf1ede0 }),
      toneMapped: false,
    }),
  );
  magnets.position.set(-0.12, 1.0, 0.362);
  group.add(magnets);

  group.position.set(x, 0, z);
  group.rotation.y = rotationY;
  state.scene.add(group);
  state.colliders.push({ minX: x - 0.5, maxX: x + 0.5, minZ: z - 0.44, maxZ: z + 0.44 });
  return group;
}

function addShelf(state: BuildState, x: number, z: number, rotationY = 0): void {
  const group = new THREE.Group();
  const wood = surface(0x4c3b30, { roughness: 0.9 });
  for (const side of [-1, 1]) {
    const upright = new THREE.Mesh(new THREE.BoxGeometry(0.06, 1.9, 0.34), wood);
    upright.position.set(side * 0.62, 0.95, 0);
    group.add(upright);
  }
  const trinkets = [MAT.green, MAT.amber, MAT.teal, MAT.red, MAT.purple];
  for (let i = 0; i < 4; i += 1) {
    const board = new THREE.Mesh(new THREE.BoxGeometry(1.3, 0.05, 0.34), wood);
    board.position.y = 0.42 + i * 0.46;
    board.castShadow = true;
    group.add(board);
    for (let j = 0; j < 3; j += 1) {
      const item = new THREE.Mesh(
        new THREE.BoxGeometry(0.16, 0.24, 0.14),
        surface(trinkets[(i + j) % trinkets.length], { roughness: 0.7 }),
      );
      item.position.set(-0.42 + j * 0.4, 0.57 + i * 0.46, 0);
      group.add(item);
    }
  }
  group.position.set(x, 0, z);
  group.rotation.y = rotationY;
  state.scene.add(group);
  const alongX = Math.abs(Math.cos(rotationY)) > 0.5;
  state.colliders.push({
    minX: x - (alongX ? 0.7 : 0.22),
    maxX: x + (alongX ? 0.7 : 0.22),
    minZ: z - (alongX ? 0.22 : 0.7),
    maxZ: z + (alongX ? 0.22 : 0.7),
  });
}

/* ------------------------------------------------------------------ *
 * Fret transportable et décor de cour
 * ------------------------------------------------------------------ */

/** L'écran cathodique : une télévision qui a menti sur son poids. */
function addCRT(state: BuildState, x: number, z: number, rotationY = 0, baseY = 0): THREE.Group {
  const group = new THREE.Group();
  const shell = surface(0xc4b791, { roughness: 0.74 });

  const body = new THREE.Mesh(new THREE.BoxGeometry(0.66, 0.56, 0.7), shell);
  body.position.y = 0.32;
  body.castShadow = true;
  group.add(body);

  const taper = new THREE.Mesh(new THREE.BoxGeometry(0.44, 0.4, 0.16), surface(0xb2a684, { roughness: 0.8 }));
  taper.position.set(0, 0.32, -0.42);
  group.add(taper);

  const bezel = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.52, 0.05), surface(0xb7ab88, { roughness: 0.7 }));
  bezel.position.set(0, 0.33, 0.36);
  group.add(bezel);

  const glass = screenPanel(0.46, 0.35, {
    title: "vga · 1997",
    lines: ["!640 x 480", "60 Hz courageux"],
    accent: MAT.teal,
  });
  glass.position.set(0, 0.35, 0.392);
  group.add(glass);

  const bloom = glowSprite(MAT.teal, 1.1, 0.1);
  bloom.position.set(0, 0.35, 0.46);
  group.add(bloom);

  const sticker = new THREE.Mesh(
    new THREE.PlaneGeometry(0.22, 0.09),
    new THREE.MeshBasicMaterial({
      map: signTexture(["NE PAS JETER"], MAT.red, { ratio: 2.4, background: 0xe6dfc9 }),
      toneMapped: false,
    }),
  );
  sticker.position.set(0.16, 0.09, 0.353);
  group.add(sticker);

  group.position.set(x, baseY, z);
  group.rotation.y = rotationY;
  state.scene.add(group);
  return group;
}

/** Le bac de périphériques : clavier, souris, tapis, et trois câbles inconnus. */
function addPeriphBin(state: BuildState, x: number, z: number, rotationY = 0, baseY = 0): THREE.Group {
  const group = new THREE.Group();

  const tub = new THREE.Mesh(
    new THREE.BoxGeometry(0.74, 0.32, 0.52),
    surface(0x3f6893, { roughness: 0.55, opacity: 0.94 }),
  );
  tub.position.y = 0.16;
  tub.castShadow = true;
  group.add(tub);

  for (const side of [-1, 1]) {
    const handle = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.09, 0.2), surface(0x2c4a69, { roughness: 0.5 }));
    handle.position.set(side * 0.39, 0.24, 0);
    group.add(handle);
  }

  const keyboard = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.05, 0.22), surface(0x11141c, { roughness: 0.55 }));
  keyboard.position.set(0, 0.345, -0.05);
  keyboard.rotation.z = 0.06;
  group.add(keyboard);

  const keys = new THREE.Mesh(
    new THREE.PlaneGeometry(0.56, 0.18),
    unlit(MAT.purple, { opacity: 0.42 }),
  );
  keys.rotation.x = -Math.PI / 2;
  keys.position.set(0, 0.375, -0.05);
  group.add(keys);

  const mouse = new THREE.Mesh(
    cachedGeometry("binMouse", () => new THREE.SphereGeometry(0.055, 10, 7)),
    surface(0x1a1f29, { roughness: 0.45 }),
  );
  mouse.scale.set(1, 0.68, 1.35);
  mouse.position.set(0.22, 0.36, 0.14);
  group.add(mouse);

  const cable = new THREE.Mesh(
    tube(
      [
        [-0.24, 0.35, 0.1],
        [-0.05, 0.42, 0.18],
        [0.14, 0.35, 0.08],
      ],
      0.018,
      "binCable",
    ),
    surface(0x0d1016, { roughness: 0.85 }),
  );
  group.add(cable);

  group.position.set(x, baseY, z);
  group.rotation.y = rotationY;
  state.scene.add(group);
  return group;
}

/** La glacière : le seul objet du jeu qui fasse un bruit de marée. */
function addCooler(state: BuildState, x: number, z: number, rotationY = 0): THREE.Group {
  const group = new THREE.Group();
  const body = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.4, 0.42), surface(0xd7dee6, { roughness: 0.6 }));
  body.position.y = 0.2;
  body.castShadow = true;
  group.add(body);

  const lid = new THREE.Mesh(new THREE.BoxGeometry(0.66, 0.08, 0.46), surface(0x3f6893, { roughness: 0.5 }));
  lid.position.y = 0.44;
  group.add(lid);

  const handle = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.14, 0.28), surface(0x2b3b4d, { roughness: 0.6 }));
  handle.position.set(0.35, 0.3, 0);
  group.add(handle);

  group.position.set(x, 0, z);
  group.rotation.y = rotationY;
  state.scene.add(group);
  return group;
}

/** Bacs de recyclage : la haie de banlieue du pauvre. */
function addBins(state: BuildState, x: number, z: number): void {
  const colors = [0x2f5f3a, 0x2b4a6b];
  colors.forEach((color, index) => {
    const bin = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.94, 0.6), surface(color, { roughness: 0.82 }));
    bin.position.set(x, 0.47, z + index * 0.74);
    bin.castShadow = true;
    state.scene.add(bin);

    const lid = new THREE.Mesh(new THREE.BoxGeometry(0.66, 0.07, 0.64), surface(0x1d2530, { roughness: 0.8 }));
    lid.position.set(x, 0.97, z + index * 0.74);
    state.scene.add(lid);

    state.colliders.push({
      minX: x - 0.34,
      maxX: x + 0.34,
      minZ: z + index * 0.74 - 0.33,
      maxZ: z + index * 0.74 + 0.33,
    });
  });
}

/** Une haie de cèdres taillée par quelqu'un qui avait autre chose à faire. */
function addHedge(state: BuildState, x: number, z: number, length: number, alongZ = true): void {
  const width = alongZ ? 0.7 : length;
  const depth = alongZ ? length : 0.7;
  const hedge = new THREE.Mesh(
    new THREE.BoxGeometry(width, 1.25, depth),
    surface(0x2c4a2f, { roughness: 0.97, flatShading: true }),
  );
  hedge.position.set(x, 0.63, z);
  hedge.castShadow = true;
  state.scene.add(hedge);
  state.colliders.push({
    minX: x - width / 2,
    maxX: x + width / 2,
    minZ: z - depth / 2,
    maxZ: z + depth / 2,
  });
}

/** Un portique d'entrée piétonne : un passage qu'on voit de loin. */
function addGateway(
  state: BuildState,
  x: number,
  z: number,
  lines: string[],
  accent: number,
  rotationY = 0,
): THREE.Group {
  const group = new THREE.Group();
  const frame = surface(0x2b323f, { metalness: 0.42, roughness: 0.55 });

  for (const side of [-1.9, 1.9]) {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.34, 3.6, 0.34), frame);
    post.position.set(side, 1.8, 0);
    post.castShadow = true;
    group.add(post);
    group.userData.postOffset = 1.9;
  }

  const header = new THREE.Mesh(new THREE.BoxGeometry(4.5, 0.6, 0.36), frame);
  header.position.y = 3.35;
  group.add(header);

  const sign = signPanel(3.9, 0.46, lines, accent, { background: 0x0d1219, kicker: "entrée piétonne" });
  sign.position.set(0, 3.35, 0.2);
  group.add(sign);

  const wash = glowSprite(accent, 4.4, 0.2);
  wash.position.set(0, 2.1, 0.3);
  group.add(wash);

  for (const side of [-1, 1]) {
    const strip = new THREE.Mesh(new THREE.BoxGeometry(0.1, 2.6, 0.06), unlit(accent, { opacity: 0.7 }));
    strip.position.set(side * 1.68, 1.5, 0.18);
    group.add(strip);
  }

  group.position.set(x, 0, z);
  group.rotation.y = rotationY;
  state.scene.add(group);

  // Les montants sont solides, le passage entre les deux ne l'est pas.
  const alongX = Math.abs(Math.cos(rotationY)) > 0.5;
  for (const side of [-1.9, 1.9]) {
    const px = alongX ? x + side : x;
    const pz = alongX ? z : z + side;
    state.colliders.push({ minX: px - 0.24, maxX: px + 0.24, minZ: pz - 0.24, maxZ: pz + 0.24 });
  }
  return group;
}

/* ------------------------------------------------------------------ *
 * Chapters
 * ------------------------------------------------------------------ */

function context(
  state: BuildState,
  spawn: THREE.Vector3,
  bounds: Collider,
  accent: number,
  fogColor: number,
  options: {
    vehicleMode?: boolean;
    spawnLookAt?: THREE.Vector3;
    fogDensity?: number;
    playerCar?: THREE.Object3D;
    playerCarExtent?: { halfX: number; halfZ: number };
  } = {},
): WorldContext {
  const updaters = state.updaters;
  return {
    scene: state.scene,
    colliders: state.colliders,
    interactables: state.interactables,
    hazards: state.hazards,
    spawn,
    spawnLookAt: options.spawnLookAt,
    bounds,
    accent,
    fogColor,
    fogDensity: options.fogDensity,
    vehicleMode: options.vehicleMode ?? false,
    playerCar: options.playerCar,
    playerCarExtent: options.playerCarExtent,
    onUpdate: updaters.length
      ? (elapsed, delta) => {
          for (const update of updaters) update(elapsed, delta);
        }
      : undefined,
  };
}

function buildPrologue(state: BuildState): WorldContext {
  const width = 22;
  const depth = 18;
  // La cour se colle contre le mur est SANS agrandir la pièce : le plafond, le
  // cadrage du menu et les décors muraux dépendent tous de `state.room`.
  const doorZ = -1.2;
  const yardMinX = width / 2 + WALL_THICKNESS / 2;
  const yardMaxX = 21.4;
  const yardHalfDepth = 8.6;
  const stowed = (id: string): boolean => state.callbacks.stowed?.(id) ?? false;

  addGround(state, width, depth, 0x5c4830, "wood");
  addRoomWalls(state, width, depth, 3.1, 0x3d4454, {
    side: "east",
    center: doorZ,
    width: 1.75,
    height: 2.4,
  });
  addLighting(state, {
    sky: 0xffe0bb,
    ground: 0x4a3f52,
    hemi: 1.35,
    ambient: 0xb2a6c0,
    ambientIntensity: 0.45,
    key: 0xffe8c8,
    keyIntensity: 2.35,
    keyPosition: [-8, 9, 8],
    fill: 0x89a6dd,
    fillIntensity: 0.6,
  });
  addCeilingLights(state, 0xffd6a3, { spacingX: 6.5, spacingZ: 8, litEvery: 2 });

  addRug(state, -1, 1.5, 8, 6.4, 0x54607e);

  // Battle station along the north wall: this is where Gisèle actually lives.
  addDesk(state, 3.4, -7.4, 7.6, 1.2, 0x302d34, 0.78);
  addMonitor(state, 1.4, -7.4, 0, {
    title: "mise à jour",
    lines: ["!99 %", "temps restant: ???"],
    accent: MAT.red,
  }, 0.78, 0.9);
  addMonitor(state, 5.2, -7.4, 0, {
    title: "discord · la gang",
    lines: ["Jean-Sébas: chu parti", "(il ment)", "!LAN 19:00"],
    accent: MAT.teal,
  }, 0.78, 0.9);
  addKeyboard(state, 3.3, -6.9, 0.83, MAT.purple);
  addMouseAndPad(state, 4.4, -6.9, 0.835);
  const chair = addChair(state, 3.3, -5.9, 0x272c39);
  chair.rotation.y = Math.PI;

  addSofa(state, -7.4, 0.6);
  addShelf(state, -10.2, 5.4, Math.PI / 2);
  addCrate(state, -9.4, -6, 1.4, 1, 0.9);

  addWallPoster(state, "west", -1.6, 1.9, "NO SLEEP\nTILL DHCP", "tournée mondiale du sous-sol", MAT.purple, 11, 1.35);
  addWallPoster(state, "west", 2.4, 1.9, "GISÈLE\nREADY", "18 kg de fierté beige", MAT.teal, 23, 1.35);
  addWallSign(state, "north", -7.4, 2.35, 3.2, 0.8, ["CHÂTEAUGUAY · 18 H 42"], MAT.amber, "sortie dans");
  addWallClock(state, "east", 3.6, 2.3, 18.7);
  addVent(state, "south", -6, 2.5);

  // Gisèle herself, at the exact spot the opening camera is framed on.
  // Chaque objet transportable disparaît du plancher une fois embarqué : le
  // monde est reconstruit à chaque chargement de partie.
  if (!stowed("tour-gisèle")) {
    const pc = addPC(state, 3.8, 4.2, MAT.beige, false);
    pc.rotation.y = -1.95;
    addInteractable(state, "home-pc", "Soulever Gisèle", pc, 2.15, new THREE.Vector3(0, 0.65, 0));
    addMarker(pc, MAT.amber, 0.6);
  }
  addPointLight(state, 0xffcf83, 14, 7, 2.7, 1.9, 3.3);

  if (!stowed("sac-a-dos")) {
    const bag = removable(state, () =>
      addBackpack(state, 0.2, 5.6, { rotationY: -0.5, color: 0x3d6893, accent: MAT.green, scale: 1.18 }),
    );
    addInteractable(state, "home-bag", "Enfiler le sac à dos", bag, 1.9, new THREE.Vector3(0, 0.34, 0));
    addMarker(bag, MAT.teal, 0.5);
  }

  if (!stowed("moniteur-crt")) {
    const crt = addCRT(state, 5.9, 2.6, -1.1);
    addInteractable(state, "home-monitor", "Prendre l’écran", crt, 1.95, new THREE.Vector3(0, 0.34, 0.2));
    addMarker(crt, MAT.teal, 0.5);
  }

  if (!stowed("bac-peripheriques")) {
    const bin = addPeriphBin(state, 1.9, 2.3, 0.45);
    addInteractable(state, "home-peripherals", "Prendre le bac", bin, 1.85, new THREE.Vector3(0, 0.3, 0.15));
    addMarker(bin, MAT.purple, 0.46);
  }

  if (!stowed("glaciere")) {
    const cooler = addCooler(state, 7.2, 4.4, 0.7);
    addInteractable(state, "home-cooler", "Prendre la glacière", cooler, 1.8, new THREE.Vector3(0, 0.3, 0.1));
  }

  if (!stowed("multiprise-maison")) {
    const strip = addPowerBar(state, -2.4, -6.4);
    addInteractable(state, "home-powerbar", "Prendre la multiprise", strip, 1.8, new THREE.Vector3(0, 0.3, 0));
  }

  // La chaise de bureau : elle roule mal, elle est lourde, elle est à toi.
  if (stowed("chaise-gamer")) {
    chair.visible = false;
  } else {
    addInteractable(state, "home-chair", "Prendre la chaise", chair, 2, new THREE.Vector3(0, 0.6, 0));
  }

  const fridge = addFridge(state, 8.4, 1.4, -Math.PI / 2);
  addInteractable(state, "home-fridge", "Faire le plein", fridge, 2.1, new THREE.Vector3(0, 1, 0.45));
  addMarker(fridge, MAT.blue, 0.6);

  const door = addApartmentDoor(state, 11, -1.2);
  addInteractable(state, "home-door", "Ouvrir la porte", door, 2.4, new THREE.Vector3(-0.12, 1.05, 0));

  // Un seuil pour raccorder le plancher de bois à l'asphalte de la cour.
  box(state, 11, 0.02, doorZ, 0.5, 0.04, 1.75, 0x4a3b2c, false, { castShadow: false });

  addCableRun(
    state,
    [
      [3.4, 0.05, -6.6],
      [2.2, 0.05, -3],
      [3.6, 0.05, 2.4],
    ],
    MAT.amber,
    0.04,
    "prologue",
  );

  /* --- La cour : dix mètres d'asphalte entre le salon et le coffre. --- */

  const yardCenterX = (yardMinX + yardMaxX) / 2;
  const yardWidth = yardMaxX - yardMinX;
  addGroundPatch(state, yardCenterX, 0, yardWidth, yardHalfDepth * 2, 0x3a4150, "asphalt");

  addFenceRun(state, yardCenterX, -yardHalfDepth, yardWidth, 0.4);
  addFenceRun(state, yardCenterX, yardHalfDepth, yardWidth, 0.4);
  addFenceRun(state, yardMaxX, 0, 0.4, yardHalfDepth * 2);
  addHedge(state, 12.6, 6.9, 5.2, false);
  addBins(state, 12.3, -6.6);

  // La cour est dans l'ombre portée du bloc : elle vit de ses propres lampes.
  addLampPost(state, 20.1, -6.2, true);
  addPointLight(state, 0xffd7a1, 26, 15, 12.4, 2.5, doorZ);
  addPointLight(state, 0xffe3b4, 20, 13, 16.4, 2.6, 2.8);

  const porch = new THREE.Mesh(
    cachedGeometry("porchLamp", () => new THREE.BoxGeometry(0.26, 0.32, 0.22)),
    unlit(0xffe6bd, { opacity: 0.95 }),
  );
  porch.position.set(11.42, 2.42, doorZ);
  state.scene.add(porch);
  const porchGlow = glowSprite(0xffd7a1, 4.2, 0.34);
  porchGlow.position.set(11.6, 2.4, doorZ);
  state.scene.add(porchGlow);

  // Vues de la cour, les fenêtres de l'appartement restent allumées : c'est la
  // preuve visible qu'on vient de sortir de là.
  for (const windowZ of [-5.4, 2.6, 6.2]) {
    const pane = new THREE.Mesh(
      new THREE.PlaneGeometry(1.5, 1.1),
      unlit(0xffca7d, { opacity: 0.86 }),
    );
    pane.position.set(11.19, 1.85, windowZ);
    pane.rotation.y = Math.PI / 2;
    state.scene.add(pane);

    const frame = new THREE.Mesh(
      new THREE.BoxGeometry(0.08, 1.3, 1.7),
      surface(0x2c333f, { roughness: 0.8 }),
    );
    frame.position.set(11.13, 1.85, windowZ);
    state.scene.add(frame);

    const spill = glowSprite(0xffca7d, 3, 0.16);
    spill.position.set(11.7, 1.8, windowZ);
    state.scene.add(spill);
  }

  const car = addCar(state, 17.6, 3.4, 0x8f4960, false, 0, { openTrunk: true });
  state.colliders.push({
    minX: 17.6 - CAR_HALF_LENGTH,
    maxX: 17.6 + CAR_HALF_LENGTH,
    minZ: 3.4 - CAR_HALF_WIDTH,
    maxZ: 3.4 + CAR_HALF_WIDTH,
  });
  addInteractable(state, "car-trunk", "Charger le coffre", car, 3.1, new THREE.Vector3(-2.1, 0.85, 0));
  addInteractable(state, "car-drive", "Embarquer", car, 3.1, new THREE.Vector3(-0.3, 1.1, -1.35));
  addMarker(car, MAT.orange, 1.1);

  addBillboard(state, 16.4, -7.9, ["CHÂTEAUGUAY", "18 H 42"], Math.PI);

  return context(
    state,
    new THREE.Vector3(-4, 0, 1),
    { minX: -width / 2 + WALL_THICKNESS / 2, maxX: yardMaxX + 0.2, minZ: -depth / 2 + WALL_THICKNESS / 2, maxZ: depth / 2 - WALL_THICKNESS / 2 },
    MAT.amber,
    0x2a2018,
    {
      spawnLookAt: new THREE.Vector3(2, 0, 4.9),
      fogDensity: 0.02,
    },
  );
}

function buildParking(state: BuildState): WorldContext {
  const width = 64;
  const depth = 48;
  addGround(state, width, depth, 0x262b34, "asphalt");
  addBoundaryFences(state, width, depth);
  addLighting(state, {
    sky: 0x5c72a8,
    ground: 0x191d27,
    hemi: 0.9,
    ambient: 0x7d8cb4,
    ambientIntensity: 0.38,
    key: 0xc9daff,
    keyIntensity: 1.35,
    keyPosition: [-16, 22, 14],
    fill: 0xffcf90,
    fillIntensity: 0.55,
    shadowRadius: 4,
  });

  // Three aisles of bays. The lot is the maze; the cars are the walls.
  const paintedRows = [-14, 1, 15];
  const palette = [0x64748b, 0x8f4960, 0x485f4d, 0x4f477a, 0x68493f, 0x3c6074, 0x7d7146, 0x75536d];
  paintedRows.forEach((z, rowIndex) => {
    for (let index = 0; index < 11; index += 1) {
      const x = -27 + index * 5.4;
      // The last row keeps its first two bays clear so the entrance lane works.
      const skip = (rowIndex === 0 && index === 5) || (rowIndex === 2 && index < 2);
      const occupied = !skip && (index + rowIndex * 3) % 4 !== 1;
      addParkingBay(state, x, z, occupied ? palette[(index + rowIndex) % palette.length] : undefined);
    }
  });

  for (let i = 0; i < 12; i += 1) {
    addCone(state, -8 + (i % 6) * 2.2, 7 + Math.floor(i / 6) * 1.6);
  }

  addLampPost(state, -22, -6, true);
  addLampPost(state, -2, -6, true);
  addLampPost(state, 18, -6, false);
  addLampPost(state, -22, 8, false);
  addLampPost(state, 6, 8, true);
  addLampPost(state, 24, 8, true);

  addBillboard(state, 0, -22, ["LAN → · PARKING ←", "LOGIQUE: HORS LIGNE"]);
  // Turned across the lane so it reads while driving in, not end-on.
  addFloorDecal(state, -22, 19.5, 5, 1.6, ["ENTRÉE"], MAT.green, -Math.PI / 2);
  addFloorDecal(state, 2.5, -8.6, 4, 1.4, ["P-404"], MAT.green);

  const kiosk = addKiosk(
    state,
    -25,
    5,
    { title: "borne p-404", lines: ["!48,00 $", "frais: 7,00 $", "frais d'affichage: 2 $"], accent: MAT.amber },
    1,
    0.5,
  );
  addInteractable(state, "parking-kiosk", "Payer le tribut", kiosk, 3.4, new THREE.Vector3(0, 1.35, 0.72));
  addMarker(kiosk, MAT.amber, 0.9);

  const attendant = addNPC(state, "Martine_du_P", -17, 6.5, 0xe78e55, "🦺", true, { hat: "cap" });
  addInteractable(state, "parking-attendant", "Parler à Martine", attendant, 3.2, new THREE.Vector3(0, 1.45, 0));

  const corral = addCart(state, 12, 6.6);
  addCart(state, 13.4, 7.4);
  addCart(state, 14.6, 6.2);
  if (!state.callbacks.stowed?.("diable")) {
    const dolly = removable(state, () => addHandTruck(state, 10.4, 6.9, 0.6));
    addInteractable(state, "parking-dolly", "Emprunter le diable", dolly, 3.2, new THREE.Vector3(0, 0.8, 0));
    addMarker(dolly, MAT.orange, 0.7);
  }
  corral.userData.bob = 4;

  const spot = new THREE.Group();
  const spotFloor = plane(4.6, 5.4, 0x1f3b26, 0.65);
  spotFloor.rotation.x = -Math.PI / 2;
  spotFloor.position.y = 0.019;
  spot.add(spotFloor);
  const number = new THREE.Mesh(
    new THREE.PlaneGeometry(3.6, 1.5),
    new THREE.MeshBasicMaterial({
      map: signTexture(["P-404"], MAT.green, { ratio: 2.4, background: 0x0f1c17 }),
      transparent: true,
      opacity: 0.9,
      toneMapped: false,
    }),
  );
  number.rotation.x = -Math.PI / 2;
  number.position.y = 0.03;
  spot.add(number);
  spot.position.set(2.5, 0, -14);
  state.scene.add(spot);
  addInteractable(state, "parking-spot", "Se stationner en P-404", spot, 4.2);
  addMarker(spot, MAT.green, 1.5);

  const terminal = addLoreTerminal(
    state,
    27,
    5,
    "archive 01",
    ["!LA GRANDE", "REMORQUE", "2011"],
    -0.7,
  );
  addInteractable(state, "lore-parking", "Lire l’archive", terminal, 3, new THREE.Vector3(0, 1.35, 0.55));

  const dollyLore = addLoreTerminal(
    state,
    -28,
    -19,
    "archive 08",
    ["!LE DIABLE", "DE 2014"],
    0.8,
  );
  addInteractable(state, "lore-diable", "Lire l’archive", dollyLore, 3, new THREE.Vector3(0, 1.35, 0.55));

  // Les voies de circulation, peintes pour qu'on comprenne où ça passe vite.
  addFloorDecal(state, -12, 8.6, 6, 1.5, ["→ VOIE DE CIRCULATION"], MAT.amber);
  addFloorDecal(state, 12, -6.4, 6, 1.5, ["← VOIE DE CIRCULATION"], MAT.amber);

  // Trois corps mobiles qui bloquent réellement le passage : on ne traverse pas
  // une navette, on l'attend.
  const cart = addCart(state, 0, 10);
  state.hazards.push({
    object: cart,
    axis: "x",
    origin: 0,
    amplitude: 18,
    speed: 0.38,
    phase: 0,
    radius: 1.2,
    lastHit: 0,
    damage: 3,
    message: "Un panier autonome évalue ta conduite.",
    solid: { halfX: 0.58, halfZ: 0.52 },
  });

  const roamingCar = addCar(state, 0, -5, 0xd97845, false);
  state.hazards.push({
    object: roamingCar,
    axis: "x",
    origin: 0,
    amplitude: 25,
    speed: 0.22,
    phase: Math.PI / 2,
    radius: 2.4,
    lastHit: 0,
    damage: 8,
    message: "Une Civic cherche la sortie depuis 2019.",
    solid: { halfX: CAR_HALF_LENGTH, halfZ: CAR_HALF_WIDTH },
  });

  const shuttle = addCar(state, 0, 21, 0x3f6f52, false);
  shuttle.scale.set(1.35, 1.25, 1.15);
  state.hazards.push({
    object: shuttle,
    axis: "x",
    origin: 0,
    amplitude: 27,
    speed: 0.16,
    phase: Math.PI,
    radius: 3,
    lastHit: 0,
    damage: 7,
    message: "La navette du LAN ne freine que pour les bénévoles.",
    solid: { halfX: CAR_HALF_LENGTH * 1.35, halfZ: CAR_HALF_WIDTH * 1.15 },
  });

  // La voiture du joueur : invisible tant qu'il est au volant, elle réapparaît
  // là où il descend. Le coffre et la portière la suivent.
  const playerCar = addCar(state, -27, 15.5, 0x8f4960, false, Math.PI / 2, { openTrunk: true });
  playerCar.visible = false;
  addInteractable(state, "car-trunk", "Ouvrir le coffre", playerCar, 3.2, new THREE.Vector3(-2.1, 0.85, 0));
  addInteractable(state, "car-board", "Reprendre le volant", playerCar, 3.2, new THREE.Vector3(-0.3, 1.1, -1.35));

  const gate = addGateway(state, 2.5, -23, ["SALLE DU LAN"], MAT.green, 0);
  addInteractable(state, "parking-exit", "Entrer dans le hall", gate, 3.6, new THREE.Vector3(0, 1.6, -0.6));
  addMarker(gate, MAT.green, 1.3);
  addFloorDecal(state, 2.5, -20.4, 5, 1.6, ["ENREGISTREMENT →"], MAT.green);

  return context(state, new THREE.Vector3(-27, 0, 15.5), interiorBounds(width, depth, 0.4), MAT.green, 0x1b2331, {
    vehicleMode: true,
    fogDensity: 0.013,
    spawnLookAt: new THREE.Vector3(6, 0, 6),
    playerCar,
    playerCarExtent: { halfX: CAR_HALF_LENGTH, halfZ: CAR_HALF_WIDTH },
  });
}

function buildRegistration(state: BuildState): WorldContext {
  const width = 50;
  const depth = 38;
  addGround(state, width, depth, 0x394054, "carpet");
  addRoomWalls(state, width, depth, 5.2, 0x3c4356);
  addLighting(state, {
    sky: 0xc4d3f2,
    ground: 0x3a4152,
    hemi: 1.4,
    ambient: 0xa4b0cc,
    ambientIntensity: 0.42,
    key: 0xf2f5ff,
    keyIntensity: 2.6,
    keyPosition: [-12, 16, 12],
    fill: 0x7f9fd8,
    fillIntensity: 0.62,
  });
  addCeilingLights(state, 0xe9edff, { spacingX: 10, spacingZ: 11, litEvery: 4 });

  addDesk(state, 0, -13.5, 24, 1.5, 0x394052, 0.95);
  addBanner(state, 0, 4.1, -17.4, 14, 2.4, ["ENREGISTREMENT", "badges · bracelets · panique"], MAT.teal);

  addWallScreen(state, "north", -16, 3.2, 4.2, 2.4, {
    title: "affichage officiel",
    lines: ["mot du jour", "!CASTOR", "hier: ORIGNAL"],
    accent: MAT.amber,
  });
  addWallScreen(state, "north", 16, 3.2, 4.2, 2.4, {
    title: "plan de salle",
    lines: ["rangée G · 404", "→ contrôle → salle", "!SUIS LA LIGNE VERTE"],
    accent: MAT.green,
  });
  addWallSign(state, "west", -6, 3, 4.6, 1.1, ["FILE D’ATTENTE →"], MAT.teal, "patience obligatoire");
  addWallClock(state, "east", -2, 3.4, 19.4);
  addVent(state, "east", 12, 3.6);
  addPipes(state, "west", 8, 20, 4.6);

  // A green line on the floor that actually leads to the exit.
  for (let z = 10; z > -12; z -= 3) {
    const dash = plane(0.28, 1.6, MAT.green, 0.4);
    dash.rotation.x = -Math.PI / 2;
    dash.position.set(-3, 0.02, z);
    state.scene.add(dash);
  }

  const charger = addKiosk(
    state,
    -18,
    -6,
    { title: "station de charge", lines: ["!3 %", "câble: USB-ish"], accent: MAT.amber },
    1,
    0.4,
  );
  addInteractable(state, "phone-charger", "Réanimer le téléphone", charger, 2.5, new THREE.Vector3(0, 1.35, 0.72));
  addMarker(charger, MAT.amber, 0.75);

  const clerk = addNPC(state, "Mélanie.exe", 0, -11.2, 0x8460aa, "🪪", true, { hat: "cap" });
  addInteractable(state, "registration-clerk", "Compiler le badge", clerk, 3, new THREE.Vector3(0, 1.45, 0));
  addMarker(clerk, MAT.green, 0.68);

  const help = addNPC(state, "Bénévole_12", 13, -11.2, 0x3e7b6e, "🫡", true, { hat: "cap" });
  addInteractable(state, "registration-helper", "Demander de l’aide", help, 2.8, new THREE.Vector3(0, 1.45, 0));

  // Serpentine queue. It is longer than it needs to be, like every real one.
  addBarrierQueue(state, -12, -6, 22, MAT.teal);
  addBarrierQueue(state, -12, -2, 22, MAT.teal);
  addBarrierQueue(state, -12, 2, 22, MAT.teal);

  const queueNames = ["QR_Perdu", "MamanJarrive", "xX_NoScope_Xx", "ClavierBleu", "Pingouin", "AFK_Dave", "La_Gang", "Ti-Guy"];
  const queueIcons = ["📱", "😬", "🎧", "⌨️", "🐧", "💤", "🍕", "📺"];
  const queueShirts = [0x657ca8, 0xa86f65, 0x6da865, 0x9365a8, 0xa89b65, 0x65a89c, 0xa8657f, 0x7a86a8];
  for (let i = 0; i < 8; i += 1) {
    addNPC(state, queueNames[i], -10 + i * 2.6, -4 + (i % 3) * 4, queueShirts[i], queueIcons[i], true, {
      hat: i % 3 === 0 ? "headset" : i % 3 === 1 ? "cap" : "none",
      skin: SKIN_TONES[i % SKIN_TONES.length],
      hair: HAIR_TONES[(i + 1) % HAIR_TONES.length],
      rotationY: Math.PI + (i % 2) * 0.3,
    });
  }

  const cutter = addNPC(state, "Le_Faufileux", 8, 1.5, 0xc27a3f, "🦊", true, {
    hat: "cap",
    rotationY: -1.2,
  });
  addInteractable(state, "registration-queue", "Régler le cas du faufileux", cutter, 2.8, new THREE.Vector3(0, 1.45, 0));
  addMarker(cutter, MAT.orange, 0.6);

  for (const x of [-20, 20]) {
    addBench(state, x, 8, 5.2);
    addBench(state, x, 13, 5.2);
  }

  const board = new THREE.Group();
  const boardBody = new THREE.Mesh(new THREE.BoxGeometry(4, 2.4, 0.22), surface(0x151b26, { roughness: 0.5 }));
  boardBody.position.y = 2.5;
  boardBody.castShadow = true;
  board.add(boardBody);
  const boardScreen = screenPanel(3.6, 2, {
    title: "écran officiel",
    lines: ["MOT DU JOUR", "!CASTOR", "badges perdus = monnaie"],
    accent: MAT.amber,
  });
  boardScreen.position.set(0, 2.5, 0.12);
  board.add(boardScreen);
  const boardGlow = glowSprite(MAT.amber, 5, 0.18);
  boardGlow.position.set(0, 2.5, 0.5);
  board.add(boardGlow);
  for (const side of [-1, 1]) {
    const leg = new THREE.Mesh(new THREE.BoxGeometry(0.12, 1.4, 0.12), surface(0x2a3140, { metalness: 0.5 }));
    leg.position.set(side * 1.5, 0.7, 0);
    board.add(leg);
  }
  board.position.set(17, 0, 4);
  board.rotation.y = -0.9;
  state.scene.add(board);
  state.colliders.push({ minX: 15.6, maxX: 18.4, minZ: 3.2, maxZ: 4.8 });
  addInteractable(state, "registration-board", "Lire l’écran", board, 3.2, new THREE.Vector3(0, 2.2, 0.4));
  addMarker(board, MAT.amber, 1.1);

  const terminal = addLoreTerminal(state, -21, 14, "archive 02", ["!LE BADGE", "MAUDIT"], 0.6);
  addInteractable(state, "lore-lobby", "Lire l’archive", terminal, 2.4, new THREE.Vector3(0, 1.35, 0.55));

  const exit = addSecurityDoor(state, 0, sealedExitOffset(depth), MAT.red, 0, true);
  addInteractable(state, "registration-exit", "Aller au contrôle", exit, 2.9, new THREE.Vector3(0, 1.7, 0));

  return context(state, new THREE.Vector3(0, 0, 14), interiorBounds(width, depth), MAT.teal, 0x272e40, {
    fogDensity: 0.016,
  });
}

function buildCheckpoint(state: BuildState): WorldContext {
  const width = 48;
  const depth = 30;
  addGround(state, width, depth, 0x333a4b, "concrete");
  addRoomWalls(state, width, depth, 4.6, 0x373e4e);
  addLighting(state, {
    sky: 0xa9b8dc,
    ground: 0x363042,
    hemi: 1.3,
    ambient: 0x9dabca,
    ambientIntensity: 0.4,
    key: 0xffeef0,
    keyIntensity: 2.35,
    keyPosition: [-10, 14, 10],
    fill: 0xff9fae,
    fillIntensity: 0.5,
  });
  addCeilingLights(state, 0xffdfe4, { spacingX: 10, spacingZ: 12, litEvery: 3 });

  addBarrierQueue(state, -18, 8, 26);
  addBarrierQueue(state, -18, 3, 26);
  addBarrierQueue(state, -18, -2, 26);
  addFloorDecal(state, -6, 11.4, 7, 1.6, ["← FILE UNIQUE"], MAT.red);

  addWallSign(state, "north", -8, 3, 6, 1.4, ["CONTRÔLE DES SACS"], MAT.red, "zone parodique");
  addWallScreen(state, "north", 8, 3, 4.4, 2.5, {
    title: "règlement fictif",
    lines: ["liquides > 100 ml:", "!PILOTE SIGNÉ REQUIS", "tours beiges: tolérées"],
    accent: MAT.red,
  });
  addVent(state, "west", 6, 3.4);
  addPipes(state, "south", 0, 26, 4.1);
  addWallClock(state, "east", 6, 3.2, 20.1);

  addDesk(state, -16, -9.5, 10, 2.4, 0x4b3e38, 0.86);
  const bag = addBackpack(state, -16, -9.5, {
    baseY: 0.9,
    scale: 1.15,
    open: true,
    color: 0x3d6893,
    accent: MAT.green,
  });
  addInteractable(state, "packing-table", "Préparer le sac", bag, 2.8, new THREE.Vector3(0, 0.36, 0));
  addMarker(bag, MAT.amber, 0.6);

  const stash = addPC(state, -11.4, -9.4, MAT.beige, true, 0.86);
  stash.rotation.y = 0.35;
  addInteractable(state, "checkpoint-stash", "Ouvrir le panneau de Gisèle", stash, 2.5, new THREE.Vector3(0, 0.6, 0));
  addMarker(stash, MAT.purple, 0.65);

  const arch = addScannerArch(state, 6, -6);
  addInteractable(state, "security-arch", "Passer le contrôle", arch, 3, new THREE.Vector3(0, 1.5, 0));
  addMarker(arch, MAT.red, 0.9);

  const guard = addNPC(state, "Agent Pare-Feu", 12, -5, 0x7c454c, "🛡️", true, { hat: "cap" });
  addInteractable(state, "security-guard", "Observer l’agent", guard, 2.6, new THREE.Vector3(0, 1.45, 0));

  addNPC(state, "Sac_Transparent", -4, 5, 0x596d82, "😇", true, { hat: "none", rotationY: 0.4 });
  addNPC(state, "Très_Subtil", -1, -1, 0x6e8259, "🥸", true, { hat: "cap", rotationY: -0.8 });
  addNPC(state, "Roxy", 2.4, 6.5, 0x9c5f86, "📋", true, { hat: "headset", rotationY: 2.2 });

  const surrender = addCrate(state, 18, 8, 1.8, 1.4, 1.1);
  const binLid = new THREE.Mesh(new THREE.BoxGeometry(1.9, 0.08, 1.5), surface(0x2c3240, { metalness: 0.4 }));
  binLid.position.y = 1.16;
  surrender.add(binLid);
  const binSign = signPanel(1.5, 0.5, ["BAC DES REGRETS"], MAT.red, { background: 0x151b26 });
  binSign.position.set(0, 0.6, 0.71);
  surrender.add(binSign);
  addInteractable(state, "surrender-bin", "Inspecter le bac", surrender, 2.5, new THREE.Vector3(0, 1.25, 0.5));

  const terminal = addLoreTerminal(state, -22, 11, "archive 03", ["!TRAITÉ DES", "LIQUIDES"], 0.5);
  addInteractable(state, "lore-checkpoint", "Lire l’archive", terminal, 2.4, new THREE.Vector3(0, 1.35, 0.55));

  const door = addSecurityDoor(state, sealedExitOffset(width), -7, MAT.green, Math.PI / 2, true, "SALLE");
  addInteractable(state, "checkpoint-exit", "Entrer dans le LAN", door, 2.9, new THREE.Vector3(0, 1.7, 0));

  return context(state, new THREE.Vector3(-21, 0, 9), interiorBounds(width, depth), MAT.red, 0x242a38, {
    fogDensity: 0.017,
  });
}

function buildHall(state: BuildState): WorldContext {
  const width = 84;
  const depth = 64;
  addGround(state, width, depth, 0x272d40, "carpet");
  addRoomWalls(state, width, depth, 8, 0x2c3345);
  addLighting(state, {
    sky: 0x6d7bc4,
    ground: 0x2b3048,
    hemi: 1.2,
    ambient: 0x97a2cf,
    ambientIntensity: 0.4,
    key: 0xd7dcff,
    keyIntensity: 2,
    keyPosition: [-18, 26, 16],
    fill: 0xa777ff,
    fillIntensity: 0.62,
    shadowRadius: 4,
  });
  addCeilingLights(state, 0xb9a7ff, { spacingX: 16, spacingZ: 18, litEvery: 4 });

  for (const z of [-22, -6, 10, 24]) {
    addTruss(state, z, width - 8, 7.1);
  }
  const shaftColors = [MAT.purple, MAT.teal, MAT.pink, MAT.blue, MAT.green];
  for (let i = 0; i < 10; i += 1) {
    const x = -34 + i * 7.6;
    const z = i % 2 ? -14 : 16;
    addSpotFixture(state, x, z, 7.1, shaftColors[i % shaftColors.length]);
    addLightShaft(state, x, z, shaftColors[i % shaftColors.length], 3.4, 7, 0.05);
  }

  // Six rows of stations. The middle aisle is the only clean path to G-404.
  for (let row = 0; row < 6; row += 1) {
    const z = -24 + row * 9.6;
    for (let index = 0; index < 8; index += 1) {
      const x = -31.5 + index * 9;
      if (Math.abs(x) < 4.5 && row > 1) continue;
      addLanStation(state, x, z, row + index, { crowd: (row + index) % 3 !== 0, seed: row * 17 + index * 5 });
    }
  }

  for (let i = 0; i < 14; i += 1) {
    const x = -30 + (i % 7) * 9.4;
    const z = -19 + Math.floor(i / 7) * 19;
    addBackpack(state, x, z, {
      rotationY: i * 0.7,
      color: [0x2c4763, 0x53324a, 0x2f4a3a, 0x4a4030][i % 4],
      accent: [MAT.green, MAT.pink, MAT.teal, MAT.amber][i % 4],
      scale: 0.95,
    });
  }

  addCableTray(state, 0, -29.2, 70);
  addCableTray(state, 0, 29.2, 70);
  for (let i = 0; i < 6; i += 1) {
    addCableRun(
      state,
      [
        [-36 + i * 14, 0.05, -28],
        [-33 + i * 14, 0.05, -8],
        [-36 + i * 14, 0.05, 12],
        [-34 + i * 14, 0.05, 28],
      ],
      [MAT.blue, MAT.green, MAT.amber, MAT.red, MAT.purple, MAT.teal][i],
      0.05,
      `hall|${i}`,
    );
  }

  addBanner(state, 0, 5.4, -31.2, 20, 3.4, ["LAN QUÉBEC 404", "1 200 joueurs · 3 rallonges"], MAT.purple);
  addWallScreen(state, "west", 0, 4.2, 7, 3.9, {
    title: "diffusion principale",
    lines: ["FINALE DANS 6 H", "!RANGÉE G → DROITE", "rallonges: rupture"],
    accent: MAT.teal,
  });
  addWallScreen(state, "east", -8, 4.2, 6, 3.4, {
    title: "classement",
    lines: ["1. Kevin_2.4GHz", "2. Kevin_5GHz", "!3. toi (absent)"],
    accent: MAT.pink,
  });
  addWallSign(state, "east", 14, 4, 6, 1.4, ["RANGÉE G · 404 →"], MAT.green, "tu y es presque");
  addWallClock(state, "north", 26, 5, 22.3);
  addVent(state, "south", -20, 5);
  addPipes(state, "north", -34, 22, 6.6);

  const coffee = addCoffeeMachine(state, -37, -24, Math.PI / 2);
  addInteractable(state, "coffee-machine", "Brasser un café", coffee, 2.6, new THREE.Vector3(0.5, 1.35, 0));
  addMarker(coffee, MAT.amber, 0.8);
  addVendingMachine(
    state,
    -37,
    -21,
    Math.PI / 2,
    { title: "distributrice", lines: ["!VIDE", "depuis 2019"], accent: MAT.red, ratio: 0.7 },
    0x2f4a63,
  );

  addDesk(state, 36, -24, 9.5, 2.2, 0x344152, 0.86);
  const techNpc = addNPC(state, "TechnoDave", 36, -21.5, 0x4f79a0, "🧰", true, { hat: "cap" });
  addInteractable(state, "tech-desk", "Négocier une rallonge", techNpc, 2.8, new THREE.Vector3(0, 1.45, 0));
  addMarker(techNpc, MAT.teal, 0.62);
  for (let i = 0; i < 5; i += 1) {
    const spool = new THREE.Mesh(
      cachedGeometry("spool", () => new THREE.CylinderGeometry(0.4, 0.4, 0.22, 16)),
      surface(MAT.orange, { roughness: 0.6 }),
    );
    spool.position.set(33 + i * 1.5, 0.99, -24);
    spool.rotation.x = Math.PI / 2;
    state.scene.add(spool);
  }

  const cableCrate = addCrate(state, 34, 25, 2.1, 1.4, 0.8);
  addCablePile(cableCrate);
  addInteractable(state, "cable-crate", "Démêler un câble", cableCrate, 2.6, new THREE.Vector3(0, 0.8, 0));
  addMarker(cableCrate, MAT.green, 0.75);

  const barter = addNPC(state, "MagesseDuSnack", -36, 22, 0x9a6a85, "🥤", true, { hat: "none" });
  addInteractable(state, "snack-mage", "Parler à la Magesse", barter, 2.8, new THREE.Vector3(0, 1.45, 0));
  addVendingMachine(
    state,
    -36,
    25,
    0,
    { title: "mana bleue", lines: ["!2 POUR 9 $", "fermentation arcanique"], accent: MAT.blue, ratio: 0.7 },
    0x24406b,
  );

  // The gang, holding a row that is being eaten alive by strangers.
  const squad = addNPC(state, "Jean-Sébas", 3.4, -28, 0x3f7d6b, "🍕", true, { hat: "headset" });
  addNPC(state, "Ti-Guy", 5.2, -27.4, 0x8a6a3f, "📺", true, { hat: "cap", rotationY: -0.6 });
  addNPC(state, "Roxy", 1.6, -27.2, 0x9c5f86, "📋", true, { hat: "none", rotationY: 0.5 });
  addInteractable(state, "hall-friends", "Retrouver la gang", squad, 3, new THREE.Vector3(0, 1.45, 0));
  addMarker(squad, MAT.pink, 0.7);

  const usb = addUSB(state, 3.5, -2);
  addInteractable(state, "secret-usb", "Ramasser l’USB louche", usb, 1.8, new THREE.Vector3(0, 0.4, 0));

  const terminalA = addLoreTerminal(state, -39, 2, "archive 04", ["!LA NUIT", "DU 56K"], Math.PI / 2);
  addInteractable(state, "lore-hallA", "Lire l’archive", terminalA, 2.4, new THREE.Vector3(0, 1.35, 0.55));
  const terminalB = addLoreTerminal(state, 39, 5, "archive 05", ["!FANTÔME", "DU RANG G"], -Math.PI / 2);
  addInteractable(state, "lore-hallB", "Lire l’archive", terminalB, 2.4, new THREE.Vector3(0, 1.35, 0.55));
  const terminalC = addLoreTerminal(state, -12, 29, "archive 09", ["!GUERRE DES", "MULTIPRISES"], Math.PI);
  addInteractable(state, "lore-multiprise", "Lire l’archive", terminalC, 2.4, new THREE.Vector3(0, 1.35, 0.55));

  const target = new THREE.Group();
  const pad = plane(6.4, 4.6, 0x22402c, 0.7);
  pad.rotation.x = -Math.PI / 2;
  pad.position.y = 0.021;
  target.add(pad);
  const padSign = new THREE.Mesh(
    new THREE.PlaneGeometry(5, 1.6),
    new THREE.MeshBasicMaterial({
      map: signTexture(["RANGÉE G · 404"], MAT.green, { ratio: 3.2, background: 0x101d16 }),
      transparent: true,
      opacity: 0.92,
      toneMapped: false,
    }),
  );
  padSign.rotation.x = -Math.PI / 2;
  padSign.position.y = 0.03;
  target.add(padSign);
  target.position.set(0, 0, 27);
  state.scene.add(target);
  addInteractable(state, "reserved-seat", "Atteindre la place 404", target, 3.6);
  addMarker(target, MAT.green, 1.4);
  addBanner(state, 0, 4.6, 30.4, 12, 2.6, ["RANGÉE G", "places 380 à 420"], MAT.green, Math.PI);

  const streamerA = addNPC(state, "LIVE!!!", 0, -3, 0xa64672, "📹", false, { hat: "cap" });
  state.hazards.push({
    object: streamerA,
    axis: "x",
    origin: 0,
    amplitude: 33,
    speed: 0.31,
    phase: 0,
    radius: 1.5,
    lastHit: 0,
    damage: 6,
    message: "Tu viens d’apparaître dans un stream vertical.",
  });

  const streamerB = addNPC(state, "SUBGOAL", 0, 6, 0x7b5aa6, "🤳", false, { hat: "headset" });
  state.hazards.push({
    object: streamerB,
    axis: "x",
    origin: 0,
    amplitude: 31,
    speed: 0.27,
    phase: Math.PI,
    radius: 1.5,
    lastHit: 0,
    damage: 6,
    message: "Un influenceur te demande de refaire ton entrée.",
  });

  const crtCarrier = addNPC(state, "CRT_MAN", 0, 20, 0x6f7a4a, "📺", false, { hat: "none" });
  state.hazards.push({
    object: crtCarrier,
    axis: "x",
    origin: 0,
    amplitude: 29,
    speed: 0.21,
    phase: Math.PI / 3,
    radius: 1.7,
    lastHit: 0,
    damage: 7,
    message: "Quelqu’un transporte un écran cathodique à l’aveugle.",
  });

  return context(state, new THREE.Vector3(0, 0, -30), interiorBounds(width, depth), MAT.purple, 0x1d2136, {
    fogDensity: 0.011,
  });
}

function buildSeat(state: BuildState): WorldContext {
  const width = 50;
  const depth = 36;
  addGround(state, width, depth, 0x2a3043, "carpet");
  addRoomWalls(state, width, depth, 6, 0x303748);
  addLighting(state, {
    sky: 0x8c6f9e,
    ground: 0x312b40,
    hemi: 1.2,
    ambient: 0xa398b6,
    ambientIntensity: 0.4,
    key: 0xffd9df,
    keyIntensity: 2.1,
    keyPosition: [-10, 18, 12],
    fill: 0xff7286,
    fillIntensity: 0.55,
  });
  addCeilingLights(state, 0xffc2cc, { spacingX: 13, spacingZ: 15, litEvery: 3 });
  addTruss(state, -4, width - 8, 5.2);
  addTruss(state, 12, width - 8, 5.2);

  for (let z = -12; z <= 12; z += 8) {
    for (let index = 0; index < 5; index += 1) {
      const x = -18 + index * 9;
      // Keep the lane to the arena open; the trial needs an audience, not a wall.
      if (z === 4 && Math.abs(x) < 5) continue;
      addLanStation(state, x, z, index + z, { crowd: (index + z) % 2 === 0, seed: index * 13 + z });
    }
  }

  const arena = new THREE.Mesh(
    new THREE.RingGeometry(4.4, 5, 64),
    unlit(MAT.red, { opacity: 0.42, side: THREE.DoubleSide }),
  );
  arena.rotation.x = -Math.PI / 2;
  arena.position.set(0, 0.04, 9);
  arena.userData.spin = 0.2;
  state.scene.add(arena);
  addLightShaft(state, 0, 9, MAT.red, 3.6, 5.6, 0.055);
  addSpotFixture(state, 0, 9, 5.4, MAT.red);

  addWallScreen(state, "north", 0, 3.6, 6.4, 3.6, {
    title: "tribunal de la chaise",
    lines: ["cause 404 vs KEVIN", "!PREUVES: 0 / 3", "témoin: AFK"],
    accent: MAT.red,
  });
  addWallSign(state, "west", 0, 3.4, 5, 1.2, ["RANGÉE G"], MAT.green, "places 380-420");
  addWallClock(state, "east", 0, 3.6, 0.9);

  const kevin = addNPC(state, "Kevin_2.4GHz", 0, 9, 0xc75e55, "🧀", true, { hat: "cap", scale: 1.14 });
  addInteractable(state, "kevin", "Confronter Kevin", kevin, 3, new THREE.Vector3(0, 1.45, 0));
  addMarker(kevin, MAT.red, 0.8);

  const chipBag = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.44, 0.16), surface(MAT.orange, { roughness: 0.5 }));
  chipBag.position.set(1.1, 0.95, 9.4);
  chipBag.rotation.z = 0.2;
  state.scene.add(chipBag);

  const witness = addNPC(state, "Témoin_AFK", -8, 9, 0x576985, "😶", true, { hat: "headset" });
  addInteractable(state, "seat-witness", "Interroger le témoin", witness, 2.6, new THREE.Vector3(0, 1.45, 0));
  addMarker(witness, MAT.teal, 0.6);

  const chair = addChair(state, 2.6, 9.6, MAT.red);
  addInteractable(state, "claimed-chair", "Examiner la chaise", chair, 2.2, new THREE.Vector3(0, 0.75, 0));
  addMarker(chair, MAT.amber, 0.6);

  // A ceiling camera nobody thought to ask about.
  const camera = new THREE.Group();
  const arm = new THREE.Mesh(new THREE.BoxGeometry(0.08, 1.4, 0.08), surface(0x2a3140, { metalness: 0.5 }));
  arm.position.y = 5.2;
  camera.add(arm);
  const bodyMesh = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.3, 0.66), surface(0x1b2230, { metalness: 0.4 }));
  bodyMesh.position.y = 4.4;
  camera.add(bodyMesh);
  const lens = new THREE.Mesh(
    cachedGeometry("camLens", () => new THREE.CylinderGeometry(0.11, 0.13, 0.2, 14)),
    surface(0x090d14, { roughness: 0.2, metalness: 0.6 }),
  );
  lens.rotation.x = Math.PI / 2;
  lens.position.set(0, 4.4, 0.4);
  camera.add(lens);
  const recording = new THREE.Mesh(
    cachedGeometry("camLed", () => new THREE.SphereGeometry(0.04, 8, 6)),
    unlit(MAT.red),
  );
  recording.position.set(0.18, 4.52, 0.3);
  recording.userData.pulse = true;
  camera.add(recording);
  camera.position.set(-3.4, 0, 13);
  state.scene.add(camera);
  state.updaters.push((elapsed) => {
    camera.rotation.y = Math.sin(elapsed * 0.35) * 0.5;
  });
  addInteractable(state, "seat-camera", "Demander la vidéo", camera, 3.4, new THREE.Vector3(0, 4.4, 0));
  addMarker(camera, MAT.purple, 0.7);

  const terminal = addLoreTerminal(state, 20, 14, "archive 06", ["!CODE DU", "SIÈGE"], -0.8);
  addInteractable(state, "lore-seat", "Lire l’archive", terminal, 2.4, new THREE.Vector3(0, 1.35, 0.55));
  const registry = addLoreTerminal(state, -20, -13, "archive 10", ["!REGISTRE DES", "PLACES VOLÉES"], 0.8);
  addInteractable(state, "lore-registre", "Lire l’archive", registry, 2.4, new THREE.Vector3(0, 1.35, 0.55));

  return context(state, new THREE.Vector3(0, 0, -15), interiorBounds(width, depth), MAT.red, 0x231f33, {
    fogDensity: 0.014,
  });
}

function buildNetwork(state: BuildState): WorldContext {
  const width = 42;
  const depth = 34;
  addGround(state, width, depth, 0x1e2534, "concrete");
  addRoomWalls(state, width, depth, 5.4, 0x27303f);
  addLighting(state, {
    sky: 0x3f7f7a,
    ground: 0x24313c,
    hemi: 1.1,
    ambient: 0x86b2bd,
    ambientIntensity: 0.38,
    key: 0xd4fff6,
    keyIntensity: 1.9,
    keyPosition: [-8, 16, 12],
    fill: 0xff7286,
    fillIntensity: 0.48,
  });
  addCeilingLights(state, 0x8ff0e0, { spacingX: 14, spacingZ: 16, litEvery: 2 });

  addDesk(state, 0, 7, 12, 2.1, 0x313242, 0.86);
  addMonitor(state, -3, 6.4, 0, {
    title: "gisèle",
    lines: ["!NO DHCP", "169.254.x.x"],
    accent: MAT.red,
  }, 0.86, 0.95);
  addMonitor(state, 3, 6.4, 0, {
    title: "bios 2009",
    lines: ["mémoire: probablement", "!BOOT?"],
    accent: MAT.amber,
  }, 0.86, 0.95);
  const pc = addPC(state, 5.6, 7, MAT.beige, true, 0);
  pc.rotation.y = -0.12;

  const power = addPowerBar(state, -7, 3.2);
  addInteractable(state, "network-power", "Brancher la rallonge", power, 2.3, new THREE.Vector3(0, 0.25, 0));
  addMarker(power, MAT.amber, 0.62);

  const networkPort = addNetworkPort(state, 8, 3.2);
  addInteractable(state, "network-cable", "Connecter le Cat-5e", networkPort, 2.3, new THREE.Vector3(0, 0.75, 0));
  addMarker(networkPort, MAT.green, 0.62);

  const keyboard = addKeyboard(state, 0, 5.6, 0.91, MAT.teal);
  addInteractable(state, "network-boot", "Démarrer Gisèle", keyboard, 2.3, new THREE.Vector3(0, 0.05, 0));
  addMarker(keyboard, MAT.teal, 0.66);
  addChair(state, 0, 8.8, MAT.red);

  const router = addRouterBoss(state, 0, -8);
  addInteractable(state, "router-boss", "Défier Gérard-DHCP", router, 4.6, new THREE.Vector3(0, 3.2, 0));
  addMarker(router, MAT.red, 2.6);
  addLightShaft(state, 0, -8, MAT.red, 5, 5.2, 0.05);

  addWallScreen(state, "west", -6, 3.2, 5, 2.8, {
    title: "journal système",
    lines: ["DHCPDISCOVER…", "DHCPDISCOVER…", "!AUCUNE RÉPONSE"],
    accent: MAT.teal,
  });
  addWallScreen(state, "east", 4, 3.2, 5, 2.8, {
    title: "bail",
    lines: ["client: GISÈLE", "!REFUSÉ", "motif: rancune"],
    accent: MAT.red,
  });
  addPipes(state, "north", 0, 24, 4.5);
  addWallClock(state, "south", 10, 3.4, 3.8);

  for (let i = 0; i < 6; i += 1) {
    const rack = box(state, -18 + (i % 3) * 1.6, 1.1, -14 + Math.floor(i / 3) * 3, 1.2, 2.2, 0.9, 0x171d29, true, {
      metalness: 0.4,
      roughness: 0.5,
    });
    for (let unit = 0; unit < 5; unit += 1) {
      const led = new THREE.Mesh(
        cachedGeometry("rackLed", () => new THREE.BoxGeometry(0.5, 0.03, 0.02)),
        unlit(unit % 2 ? MAT.green : MAT.amber, { opacity: 0.9 }),
      );
      led.position.set(rack.position.x, 0.5 + unit * 0.35, rack.position.z + 0.46);
      if (unit % 2) led.userData.pulse = true;
      state.scene.add(led);
    }
  }

  const terminal = addLoreTerminal(state, -16, 12, "archive 07", ["!GÉRARD", "DHCP"], 0.7);
  addInteractable(state, "lore-network", "Lire l’archive finale", terminal, 2.4, new THREE.Vector3(0, 1.35, 0.55));

  addCableRun(
    state,
    [
      [-15, 0.05, 11],
      [-9, 0.05, 6],
      [-12, 0.05, 0],
      [-4, 0.05, -4],
      [0, 0.05, -7],
    ],
    MAT.amber,
    0.08,
    "network",
  );
  addCableTray(state, 0, -15.6, 30);

  return context(state, new THREE.Vector3(0, 0, 14), interiorBounds(width, depth), MAT.teal, 0x152430, {
    fogDensity: 0.018,
  });
}

function buildCreditsWorld(state: BuildState): WorldContext {
  addGround(state, 40, 32, 0x070910, "concrete");
  addLighting(state, {
    sky: 0x6f7fc0,
    ground: 0x05070c,
    hemi: 0.6,
    ambient: 0x8290bb,
    ambientIntensity: 0.3,
    key: 0xe4e9ff,
    keyIntensity: 1,
    keyPosition: [-8, 14, 10],
    fill: 0x9cff57,
    fillIntensity: 0.3,
  });

  const ringColors = [MAT.green, MAT.teal, MAT.purple, MAT.amber, MAT.pink];
  for (let i = 0; i < 22; i += 1) {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(1 + (i % 3) * 0.5, 0.1, 8, 26),
      unlit(ringColors[i % ringColors.length], { opacity: 0.7 }),
    );
    ring.position.set(Math.sin(i * 2.2) * 15, 1.5 + (i % 4) * 1.3, Math.cos(i * 1.7) * 12);
    ring.rotation.set(i, i * 0.4, i * 0.2);
    ring.userData.spin = 0.35 + (i % 4) * 0.1;
    ring.userData.bob = i;
    state.scene.add(ring);

    const spark = glowSprite(ringColors[i % ringColors.length], 2.4, 0.2);
    spark.position.copy(ring.position);
    state.scene.add(spark);
  }

  const tower = addPC(state, 0, -2, MAT.beige, false);
  tower.scale.setScalar(1.6);
  tower.userData.spin = 0.2;
  addLightShaft(state, 0, -2, MAT.green, 2.4, 6, 0.06);

  return context(state, new THREE.Vector3(0, 0, 7), { minX: -18, maxX: 18, minZ: -14, maxZ: 14 }, MAT.green, 0x05070c, {
    fogDensity: 0.02,
  });
}
